/**
 * RAINBURG Personal Shopping Assistant
 * =====================================
 * Floating voice agent button + WebSocket action bridge.
 *
 * ACTIVATION:
 *   - Add ?agent=1 to any page URL  (session-based, persists via sessionStorage)
 *   - Or set RAINBURG_AGENT_ENABLED = true below for always-on (testing)
 *
 * SECURITY:
 *   - Disabled by default. Public visitors never see the agent.
 *   - To disable mid-session: ?agent=0 or close the tab.
 */

(function () {
  'use strict';

  // ─── FEATURE FLAG ───────────────────────────────────────────────────────────
  var AGENT_ENABLED = false;

  // ─── CONFIG ─────────────────────────────────────────────────────────────────
  var ELEVENLABS_AGENT_ID = 'agent_0601kkf5bfxzezwt8byjvd121fdv';
  var WS_BASE_URL = 'wss://agent.rainburg.de';
  var CDN_BASE = 'https://rainburg.de/cdn/shop/t/2/assets/';
  var SESSION_KEY = 'rainburg_agent_session';
  var ACTIVATION_KEY = 'rainburg_agent_active';

  // ─── ACTIVATION CHECK ──────────────────────────────────────────────────────
  var params = new URLSearchParams(window.location.search);

  if (params.get('agent') === '0') {
    sessionStorage.removeItem(ACTIVATION_KEY);
    return;
  }

  if (params.get('agent') === '1') {
    sessionStorage.setItem(ACTIVATION_KEY, '1');
  }

  var isActive = AGENT_ENABLED || sessionStorage.getItem(ACTIVATION_KEY) === '1';
  if (!isActive) return;

  // ─── AGENT CALL STATE PERSISTENCE ────────────────────────────────────────
  // Track whether the user was actively using the voice agent before navigation.
  // If so, auto-reconnect on the new page and prompt to resume.
  var CALL_STATE_KEY = 'rainburg_agent_in_call';

  function saveCallState() {
    sessionStorage.setItem(CALL_STATE_KEY, agentActive ? '1' : '0');
  }

  function wasInCall() {
    return sessionStorage.getItem(CALL_STATE_KEY) === '1';
  }

  // ─── SESSION ID ────────────────────────────────────────────────────────────
  var sessionId = sessionStorage.getItem(SESSION_KEY);
  if (!sessionId) {
    sessionId = 'ra-' + Math.random().toString(36).substr(2, 9) + '-' + Date.now().toString(36);
    sessionStorage.setItem(SESSION_KEY, sessionId);
  }

  // ─── CART PERSISTENCE ─────────────────────────────────────────────────────
  // Cart is stored in sessionStorage so it survives page navigation.
  // On each page load, we restore it to window.cart.
  var CART_STORAGE_KEY = 'rainburg_cart';

  function saveCart() {
    try {
      var cartData = JSON.stringify(window.cart || []);
      sessionStorage.setItem(CART_STORAGE_KEY, cartData);
      console.log('[Rainburg Agent] Cart saved to sessionStorage:', (window.cart || []).length, 'items');
    } catch (e) {
      console.warn('[Rainburg Agent] Failed to save cart:', e);
    }
  }

  function syncBadges(count) {
    var badges = document.querySelectorAll('.bag-count, .cart-count, .nav-bag-count, [data-cart-count], #bagCount, #cartCount');
    for (var b = 0; b < badges.length; b++) badges[b].textContent = String(count);
  }

  function restoreCart() {
    try {
      var stored = sessionStorage.getItem(CART_STORAGE_KEY);
      if (stored) {
        var items = JSON.parse(stored);
        if (Array.isArray(items) && items.length > 0) {
          window.cart = items;
          console.log('[Rainburg Agent] Cart restored from sessionStorage:', items.length, 'items');
          // Sync to page's native renderCart if available
          if (typeof window.renderCart === 'function') {
            window.renderCart();
          }
          // Always sync nav badge count (covers pages without renderCart, like About)
          syncBadges(items.length);
          return;
        }
      }
    } catch (e) {
      console.warn('[Rainburg Agent] Failed to restore cart:', e);
    }
    // Initialize empty cart if nothing stored or page doesn't have one
    if (typeof window.cart === 'undefined') {
      window.cart = [];
    }
  }

  // Restore cart immediately on page load
  restoreCart();

  // ─── CART STATE TRACKING ──────────────────────────────────────────────────
  // Track whether the native cart panel is open or closed
  var cartPanelOpen = false;

  function trackCartPanel() {
    var cartOverlayEl = document.getElementById('cartOverlay');
    if (!cartOverlayEl) return;
    // Check initial state
    cartPanelOpen = cartOverlayEl.classList.contains('open');
    // Watch for open/close changes
    var observer = new MutationObserver(function () {
      var wasOpen = cartPanelOpen;
      cartPanelOpen = cartOverlayEl.classList.contains('open');
      if (wasOpen !== cartPanelOpen) {
        console.log('[Rainburg Agent] Cart panel', cartPanelOpen ? 'OPENED' : 'CLOSED');
        // Notify WebSocket so backend knows cart state
        if (ws && ws.readyState === 1) {
          ws.send(JSON.stringify({ type: 'cart_panel_state', open: cartPanelOpen }));
        }
      }
    });
    observer.observe(cartOverlayEl, { attributes: true, attributeFilter: ['class'] });
  }

  // Run after DOM is ready (cart overlay may be injected by seasonal templates)
  setTimeout(trackCartPanel, 1000);

  // ─── PRODUCT CATALOG ───────────────────────────────────────────────────────
  // Each product has an imageMap: color name → CDN filename (without extension)
  var PRODUCTS = {
    'windbreaker':      { name: 'The Windbreaker',      season: 'spring', price: 149, category: 'jacket',  colors: ['Sage Green','Coral','Sky Blue','Sand','Black'], page: '/pages/spring-2026', pdp: '/products/the-windbreaker', imageMap: {'sage green':'windbreaker-sage','coral':'windbreaker-coral','sky blue':'windbreaker-skyblue','sand':'windbreaker-sand','black':'windbreaker-black'} },
    'rain-jacket':      { name: 'The Rain Jacket',      season: 'spring', price: 219, category: 'jacket',  colors: ['Moss','Charcoal','Navy','Off-White','Clay'], page: '/pages/spring-2026', pdp: '/products/the-rain-jacket', imageMap: {'moss':'rain-jacket-moss','charcoal':'rain-jacket-charcoal','navy':'rain-jacket-navy','off-white':'rain-jacket-offwhite','clay':'rain-jacket-clay'} },
    'trail-pants':      { name: 'Trail Pants',           season: 'spring', price: 129, category: 'pants',   colors: ['Olive','Charcoal','Sand','Black','Navy'], page: '/pages/spring-2026', pdp: '/products/trail-pants', imageMap: {'olive':'trail-pants-olive','charcoal':'trail-pants-charcoal','sand':'trail-pants-sand','black':'trail-pants-black','navy':'trail-pants-navy'} },
    'softshell':        { name: 'The Softshell',         season: 'spring', price: 179, category: 'jacket',  colors: ['Stone','Navy','Olive','Charcoal','Black'], page: '/pages/spring-2026', pdp: '/products/the-softshell', imageMap: {'stone':'softshell-stone','navy':'softshell-navy','olive':'softshell-olive','charcoal':'softshell-charcoal','black':'softshell-black'} },
    'soleil':           { name: 'The Soleil',            season: 'summer', price: 169, category: 'jacket',  colors: ['Amber','Sand','Sky Blue','White','Black'], page: '/pages/summer-2026', pdp: '/products/the-soleil', imageMap: {'amber':'soleil-amber','sand':'soleil-sand','sky blue':'soleil-slateblue','white':'soleil-white','black':'soleil-black'} },
    'linen-jacket':     { name: 'Linen Jacket',          season: 'summer', price: 199, category: 'jacket',  colors: ['Khaki','Black','Sage','Stone','Navy'], page: '/pages/summer-2026', pdp: '/products/linen-jacket', imageMap: {'khaki':'linen-khaki','black':'linen-black','sage':'linen-sage','stone':'linen-stone','navy':'linen-navy'} },
    'tramontane':       { name: 'The Tramontane',        season: 'summer', price: 189, category: 'jacket',  colors: ['Slate','Amber','Cream','Olive','Black'], page: '/pages/summer-2026', pdp: '/products/the-tramontane', imageMap: {'slate':'tramontane-slate','amber':'tramontane-amber','cream':'tramontane-cream','olive':'tramontane-olive','black':'tramontane-black'} },
    'zephyr-vest':      { name: 'Zephyr Vest',           season: 'summer', price: 139, category: 'vest',    colors: ['Amber','Sand','Navy','Olive','Black'], page: '/pages/summer-2026', pdp: '/products/zephyr-vest', imageMap: {'amber':'zephyr-amber','sand':'zephyr-sand','navy':'zephyr-navy','olive':'zephyr-olive','black':'zephyr-black'} },
    'torrent':          { name: 'The Torrent',           season: 'autumn', price: 249, category: 'jacket',  colors: ['Amber','Cream','Dark Brown','Olive','Black'], page: '/pages/autumn-2026', pdp: '/products/the-torrent', imageMap: {'amber':'torrent-amber','cream':'torrent-cream','dark brown':'torrent-darkbrown','olive':'torrent-olive','black':'torrent-black'} },
    'ember':            { name: 'The Ember',             season: 'autumn', price: 229, category: 'jacket',  colors: ['Amber','Walnut','Cream','Green','Black'], page: '/pages/autumn-2026', pdp: '/products/the-ember', imageMap: {'amber':'ember-amber','walnut':'ember-walnut','cream':'ember-cream','green':'ember-green','black':'ember-black'} },
    'gale':             { name: 'The Gale',              season: 'autumn', price: 289, category: 'jacket',  colors: ['Dark Brown','Amber','Stone','Charcoal','Black'], page: '/pages/autumn-2026', pdp: '/products/the-gale', imageMap: {'dark brown':'gale-darkbrown','amber':'gale-amber','stone':'gale-stone','charcoal':'gale-charcoal','black':'gale-black'} },
    'ridge-fleece':     { name: 'Ridge Fleece',           season: 'autumn', price: 189, category: 'fleece',  colors: ['Amber','Walnut','Stone','Forest','Black'], page: '/pages/autumn-2026', pdp: '/products/ridge-fleece', imageMap: {'amber':'ridge-amber','walnut':'ridge-walnut','stone':'ridge-stone','forest':'ridge-forest','black':'ridge-black'} },
    'shell-jacket':     { name: 'Shell Jacket',           season: 'winter', price: 279, category: 'jacket',  colors: ['Charcoal','Black','White','Green','Burgundy'], page: '/pages/winter-2026', pdp: '/products/shell-jacket', imageMap: {'charcoal':'shell-jacket-charcoal','black':'shell-jacket-black','white':'shell-jacket-white','green':'shell-jacket-green','burgundy':'shell-jacket-burgundy'} },
    'insulated-jacket': { name: 'Insulated Jacket',       season: 'winter', price: 299, category: 'jacket',  colors: ['Navy','Charcoal','Black','Olive','Steel Blue'], page: '/pages/winter-2026', pdp: '/products/insulated-jacket', imageMap: {'navy':'insulated-jacket-navy','charcoal':'insulated-jacket-charcoal','black':'insulated-jacket-black','olive':'insulated-jacket-olive','steel blue':'insulated-jacket-steelblue'} },
    'alpine-pants':     { name: 'Alpine Pants',           season: 'winter', price: 199, category: 'pants',   colors: ['Charcoal','Black','White','Green','Burgundy'], page: '/pages/winter-2026', pdp: '/products/alpine-pants', imageMap: {'charcoal':'alpine-pants-charcoal','black':'alpine-pants-black','white':'alpine-pants-white','green':'alpine-pants-green','burgundy':'alpine-pants-burgundy'} },
    'summit-pants':     { name: 'Summit Pants',           season: 'winter', price: 219, category: 'pants',   colors: ['Navy','Charcoal','Black','Olive','Steel Blue'], page: '/pages/winter-2026', pdp: '/products/summit-pants', imageMap: {'navy':'summit-pants-navy','charcoal':'summit-pants-charcoal','black':'summit-pants-black','olive':'summit-pants-olive','steel blue':'summit-pants-steelblue'} },
    'strand-sock':      { name: 'The Strand Sock',        season: 'summer', price: 19,  category: 'socks',   colors: ['Charcoal','Navy','Olive','Sand','Black'], page: '/pages/summer-2026', pdp: '/products/the-strand-sock', imageMap: {'charcoal':'strand-sock-charcoal','navy':'strand-sock-navy','olive':'strand-sock-olive','sand':'strand-sock-sand','black':'strand-sock-black'} },
    'harbor-tee':       { name: 'The Harbor Tee',         season: 'summer', price: 49,  category: 't-shirt', colors: ['White','Navy','Charcoal','Sage','Black'], page: '/pages/summer-2026', pdp: '/products/the-harbor-tee', imageMap: {'white':'harbor-tee-white','navy':'harbor-tee-navy','charcoal':'harbor-tee-charcoal','sage':'harbor-tee-sage','black':'harbor-tee-black'} },
    'dock-cap':         { name: 'The Dock Cap',           season: 'summer', price: 39,  category: 'cap',     colors: ['Navy','Sand','Olive','Charcoal','Black'], page: '/pages/summer-2026', pdp: '/products/the-dock-cap', imageMap: {'navy':'dock-cap-navy','sand':'dock-cap-sand','olive':'dock-cap-olive','charcoal':'dock-cap-charcoal','black':'dock-cap-black'} },
    'fjord-glove-m':    { name: 'The Fjord Glove (Men)',  season: 'winter', price: 45,  category: 'gloves',  colors: ['Black','Charcoal','Navy','Olive','Burgundy'], page: '/pages/winter-2026', pdp: '/products/the-fjord-glove-men', imageMap: {'black':'fjord-glove-m-black','charcoal':'fjord-glove-m-charcoal','navy':'fjord-glove-m-navy','olive':'fjord-glove-m-olive','burgundy':'fjord-glove-m-burgundy'} },
    'fjord-glove-w':    { name: 'The Fjord Glove (Women)',season: 'winter', price: 45,  category: 'gloves',  colors: ['Black','Charcoal','Navy','Cream','Burgundy'], page: '/pages/winter-2026', pdp: '/products/the-fjord-glove-women', imageMap: {'black':'fjord-glove-w-black','charcoal':'fjord-glove-w-charcoal','navy':'fjord-glove-w-navy','cream':'fjord-glove-w-cream','burgundy':'fjord-glove-w-burgundy'} },
    'nordic-beanie-m':  { name: 'The Nordic Beanie (Men)',season: 'winter', price: 35,  category: 'beanie',  colors: ['Black','Charcoal','Navy','Olive','Burgundy'], page: '/pages/winter-2026', pdp: '/products/the-nordic-beanie-men', imageMap: {'black':'nordic-beanie-m-black','charcoal':'nordic-beanie-m-charcoal','navy':'nordic-beanie-m-navy','olive':'nordic-beanie-m-olive','burgundy':'nordic-beanie-m-burgundy'} },
    'nordic-beanie-w':  { name: 'The Nordic Beanie (Women)',season:'winter', price: 35,  category: 'beanie',  colors: ['Black','Charcoal','Cream','Navy','Burgundy'], page: '/pages/winter-2026', pdp: '/products/the-nordic-beanie-women', imageMap: {'black':'nordic-beanie-w-black','charcoal':'nordic-beanie-w-charcoal','cream':'nordic-beanie-w-cream','navy':'nordic-beanie-w-navy','burgundy':'nordic-beanie-w-burgundy'} },
    'strom-crewneck':   { name: 'The Strom Crewneck',    season: 'spring', price: 99,  category: 'sweatshirt', colors: ['Sage Green','Navy','Charcoal','Sand','Black'], page: '/pages/spring-2026', pdp: '/products/the-strom-crewneck', imageMap: {'sage green':'strom-crewneck-sage','navy':'strom-crewneck-navy','charcoal':'strom-crewneck-charcoal','sand':'strom-crewneck-sand','black':'strom-crewneck-black'} },
    'hafen-hoodie':     { name: 'The Hafen Hoodie',      season: 'spring', price: 119, category: 'hoodie',  colors: ['Moss','Navy','Charcoal','Sand','Black'], page: '/pages/spring-2026', pdp: '/products/the-hafen-hoodie', imageMap: {'moss':'hafen-hoodie-moss','navy':'hafen-hoodie-navy','charcoal':'hafen-hoodie-charcoal','sand':'hafen-hoodie-sand','black':'hafen-hoodie-black'} },

    // ── KIDS COLLECTION ──────────────────────────────────────────────────────
    'puddle-jumper':    { name: 'The Puddle Jumper',     season: 'kids', price: 99,  category: 'jacket',  colors: ['Sky Blue','Forest Green','Coral','Sunshine Yellow','Lavender'], page: '/pages/kids', pdp: '/pages/kids', imageMap: {'sky blue':'puddle-jumper-sky','forest green':'puddle-jumper-forest','coral':'puddle-jumper-coral','sunshine yellow':'puddle-jumper-yellow','lavender':'puddle-jumper-purple'} },
    'forest-runner':    { name: 'The Forest Runner Pants', season: 'kids', price: 69,  category: 'pants',   colors: ['Moss','Navy','Charcoal','Stone','Brown'], page: '/pages/kids', pdp: '/pages/kids', imageMap: {'moss':'forest-runner-moss','navy':'forest-runner-navy','charcoal':'forest-runner-charcoal','stone':'forest-runner-stone','brown':'forest-runner-brown'} },
    'bear-cub':         { name: 'The Bear Cub Hoodie',   season: 'kids', price: 79,  category: 'hoodie',  colors: ['Sage','Cream','Sky Blue','Pink','Forest'], page: '/pages/kids', pdp: '/pages/kids', imageMap: {'sage':'bear-cub-sage','cream':'bear-cub-cream','sky blue':'bear-cub-sky','pink':'bear-cub-pink','forest':'bear-cub-forest'} },
    'pebble-tee':       { name: 'The Pebble Tee',        season: 'kids', price: 39,  category: 't-shirt', colors: ['White','Blue','Sage','Rose','Tan'], page: '/pages/kids', pdp: '/pages/kids', imageMap: {'white':'pebble-tee-white','blue':'pebble-tee-blue','sage':'pebble-tee-sage','rose':'pebble-tee-rose','tan':'pebble-tee-tan'} },
    'splash-tee':       { name: 'The Splash Tee',        season: 'kids', price: 34,  category: 't-shirt', colors: ['Ocean Blue','Coral','Yellow','Mint','White'], page: '/pages/kids', pdp: '/pages/kids', imageMap: {'ocean blue':'splash-tee-ocean','coral':'splash-tee-coral','yellow':'splash-tee-yellow','mint':'splash-tee-mint','white':'splash-tee-white'} },
    'trail-short':      { name: 'The Trail Short',        season: 'kids', price: 49,  category: 'pants',   colors: ['Khaki','Forest','Navy','Charcoal','Dusty Rose'], page: '/pages/kids', pdp: '/pages/kids', imageMap: {'khaki':'trail-short-khaki','forest':'trail-short-forest','navy':'trail-short-navy','charcoal':'trail-short-charcoal','dusty rose':'trail-short-dusty'} },
    'sunshine-cap':     { name: 'The Sunshine Cap',       season: 'kids', price: 32,  category: 'cap',     colors: ['Ocean','Coral','White','Mint','Yellow'], page: '/pages/kids', pdp: '/pages/kids', imageMap: {'ocean':'sunshine-cap-ocean','coral':'sunshine-cap-coral','white':'sunshine-cap-white','mint':'sunshine-cap-mint','yellow':'sunshine-cap-yellow'} },
    'cloud-sock':       { name: 'The Cloud Sock',         season: 'kids', price: 18,  category: 'socks',   colors: ['Sky Blue','Rose','Forest','Charcoal','Cream'], page: '/pages/kids', pdp: '/pages/kids', imageMap: {'sky blue':'cloud-sock-sky','rose':'cloud-sock-rose','forest':'cloud-sock-forest','charcoal':'cloud-sock-charcoal','cream':'cloud-sock-cream'} },
    'torrent-jacket-kids': { name: 'The Torrent Jacket',  season: 'kids', price: 119, category: 'jacket',  colors: ['Forest','Burgundy','Navy','Charcoal','Slate'], page: '/pages/kids', pdp: '/pages/kids', imageMap: {'forest':'torrent-jacket-forest','burgundy':'torrent-jacket-burgundy','navy':'torrent-jacket-navy','charcoal':'torrent-jacket-charcoal','slate':'torrent-jacket-slate'} },
    'ember-fleece-kids': { name: 'The Ember Fleece',      season: 'kids', price: 89,  category: 'fleece',  colors: ['Rust','Forest','Orange','Charcoal','Cream'], page: '/pages/kids', pdp: '/pages/kids', imageMap: {'rust':'ember-fleece-rust','forest':'ember-fleece-forest','orange':'ember-fleece-orange','charcoal':'ember-fleece-charcoal','cream':'ember-fleece-cream'} },
    'ridge-pants-kids': { name: 'The Ridge Pants',        season: 'kids', price: 84,  category: 'pants',   colors: ['Forest','Charcoal','Slate','Brown','Dusty Rose'], page: '/pages/kids', pdp: '/pages/kids', imageMap: {'forest':'ridge-pants-forest','charcoal':'ridge-pants-charcoal','slate':'ridge-pants-slate','brown':'ridge-pants-brown','dusty rose':'ridge-pants-dusty'} },
    'breeze-vest':      { name: 'The Breeze Vest',        season: 'kids', price: 69,  category: 'vest',    colors: ['Forest','Navy','Charcoal','Orange','Slate'], page: '/pages/kids', pdp: '/pages/kids', imageMap: {'forest':'breeze-vest-forest','navy':'breeze-vest-navy','charcoal':'breeze-vest-charcoal','orange':'breeze-vest-orange','slate':'breeze-vest-slate'} },
    'alpine-puffer':    { name: 'The Alpine Puffer',      season: 'kids', price: 159, category: 'jacket',  colors: ['Burgundy','Navy','Forest','Charcoal','Black'], page: '/pages/kids', pdp: '/pages/kids', imageMap: {'burgundy':'alpine-puffer-burgundy','navy':'alpine-puffer-navy','forest':'alpine-puffer-forest','charcoal':'alpine-puffer-charcoal','black':'alpine-puffer-black'} },
    'snowdrift-pants':  { name: 'The Snowdrift Pants',    season: 'kids', price: 129, category: 'pants',   colors: ['Navy','Forest','Charcoal','Black','Slate'], page: '/pages/kids', pdp: '/pages/kids', imageMap: {'navy':'snowdrift-pants-navy','forest':'snowdrift-pants-forest','charcoal':'snowdrift-pants-charcoal','black':'snowdrift-pants-black','slate':'snowdrift-pants-slate'} },
    'glacier-boot':     { name: 'The Glacier Boot',       season: 'kids', price: 119, category: 'boots',   colors: ['Navy','Charcoal','Forest','Burgundy','Black'], page: '/pages/kids', pdp: '/pages/kids', imageMap: {'navy':'glacier-boot-young-navy','charcoal':'glacier-boot-young-charcoal','forest':'glacier-boot-young-forest','burgundy':'glacier-boot-young-burgundy','black':'glacier-boot-young-black'} },
    'peak-boot':        { name: 'The Peak Boot',          season: 'kids', price: 139, category: 'boots',   colors: ['Navy','Charcoal','Forest','Black','Burgundy'], page: '/pages/kids', pdp: '/pages/kids', imageMap: {'navy':'peak-boot-older-navy','charcoal':'peak-boot-older-charcoal','forest':'peak-boot-older-forest','black':'peak-boot-older-black','burgundy':'peak-boot-older-burgundy'} },
    'snowpeak-beanie':  { name: 'The Snowpeak Beanie',    season: 'kids', price: 38,  category: 'beanie',  colors: ['Navy','Charcoal','Forest','Cream','Burgundy'], page: '/pages/kids', pdp: '/pages/kids', imageMap: {'navy':'snowpeak-beanie-navy','charcoal':'snowpeak-beanie-charcoal','forest':'snowpeak-beanie-forest','cream':'snowpeak-beanie-cream','burgundy':'snowpeak-beanie-burgundy'} },

    // ── TEENS COLLECTION ──────────────────────────────────────────────────────
    'tide-windbreaker':    { name: 'The Tide Windbreaker',    season: 'teens', price: 169, category: 'jacket',  colors: ['Slate Blue'], page: '/pages/teens', pdp: '/pages/teens', imageMap: {'slate blue':'tide-windbreaker-slate'} },
    'current-jogger':      { name: 'The Current Jogger',      season: 'teens', price: 109, category: 'pants',   colors: ['Charcoal'], page: '/pages/teens', pdp: '/pages/teens', imageMap: {'charcoal':'current-jogger-charcoal'} },
    'reef-tee':            { name: 'The Reef Tee',            season: 'teens', price: 49,  category: 't-shirt', colors: ['Off-White'], page: '/pages/teens', pdp: '/pages/teens', imageMap: {'off-white':'reef-tee-offwhite'} },
    'drift-short':         { name: 'The Drift Short',         season: 'teens', price: 79,  category: 'shorts',  colors: ['Navy'], page: '/pages/teens', pdp: '/pages/teens', imageMap: {'navy':'drift-short-navy'} },
    'storm-shell':         { name: 'The Storm Shell',         season: 'teens', price: 229, category: 'jacket',  colors: ['Forest'], page: '/pages/teens', pdp: '/pages/teens', imageMap: {'forest':'storm-shell-forest'} },
    'ember-cargo':         { name: 'The Ember Cargo',         season: 'teens', price: 129, category: 'pants',   colors: ['Olive'], page: '/pages/teens', pdp: '/pages/teens', imageMap: {'olive':'ember-cargo-olive'} },
    'dusk-hoodie':         { name: 'The Dusk Hoodie',         season: 'teens', price: 139, category: 'hoodie',  colors: ['Rust'], page: '/pages/teens', pdp: '/pages/teens', imageMap: {'rust':'dusk-hoodie-rust'} },
    'polar-parka':         { name: 'The Polar Parka',         season: 'teens', price: 269, category: 'jacket',  colors: ['Black'], page: '/pages/teens', pdp: '/pages/teens', imageMap: {'black':'polar-parka-black'} },
    'summit-jogger-teens': { name: 'The Summit Jogger',       season: 'teens', price: 149, category: 'pants',   colors: ['Navy'], page: '/pages/teens', pdp: '/pages/teens', imageMap: {'navy':'summit-jogger-navy'} },
    'frost-beanie':        { name: 'The Frost Beanie',        season: 'teens', price: 39,  category: 'beanie',  colors: ['Charcoal'], page: '/pages/teens', pdp: '/pages/teens', imageMap: {'charcoal':'frost-beanie-charcoal'} }
  };

  // ─── COLOR INTELLIGENCE ────────────────────────────────────────────────────
  // Comprehensive mapping: consumer color terms → Rainburg brand color names.
  // When a user says "tan" we need to match products with Sand, Khaki, Stone, etc.
  // The frontend handles ALL fuzzy matching — Claude just passes the consumer's word through.
  var COLOR_ALIASES = {
    // ── Standard colors ──
    'grey':       ['charcoal','stone','steel blue','slate'],
    'gray':       ['charcoal','stone','steel blue','slate'],
    'blue':       ['navy','sky blue','steel blue','slate'],
    'red':        ['coral','burgundy','amber'],
    'brown':      ['walnut','dark brown','clay','khaki','amber'],
    'green':      ['olive','moss','sage green','forest','green','sage'],
    'white':      ['off-white','white','cream'],
    'black':      ['black','charcoal','midnight'],
    'yellow':     ['amber','sand','khaki'],
    'orange':     ['coral','amber','clay'],
    'pink':       ['coral'],
    'purple':     ['burgundy'],

    // ── Common consumer terms for warm neutrals ──
    'beige':      ['sand','cream','stone','khaki','off-white','amber'],
    'sand':       ['sand','cream','stone','khaki','off-white','amber'],
    'tan':        ['sand','cream','stone','khaki','amber','walnut'],
    'cream':      ['cream','sand','stone','off-white','khaki'],
    'khaki':      ['khaki','sand','olive','stone'],
    'camel':      ['sand','khaki','amber','walnut'],
    'nude':       ['sand','cream','off-white','stone'],
    'oatmeal':    ['cream','stone','off-white','sand'],
    'ivory':      ['off-white','cream','white'],
    'bone':       ['off-white','cream','stone'],
    'linen':      ['cream','sand','off-white','khaki','stone'],

    // ── Earth tones ──
    'earth':      ['walnut','olive','moss','clay','dark brown','amber'],
    'earthy':     ['walnut','olive','moss','clay','dark brown','amber'],
    'rust':       ['amber','clay','coral'],
    'copper':     ['amber','clay','coral'],
    'terracotta': ['clay','coral','amber'],
    'coffee':     ['walnut','dark brown'],
    'chocolate':  ['dark brown','walnut'],
    'mocha':      ['walnut','dark brown','clay'],
    'espresso':   ['dark brown','walnut','black'],
    'sienna':     ['amber','walnut','clay'],
    'chestnut':   ['walnut','dark brown','amber'],
    'mahogany':   ['dark brown','walnut','burgundy'],
    'cinnamon':   ['amber','walnut','clay'],

    // ── Cool neutrals ──
    'taupe':      ['stone','charcoal','khaki'],
    'pewter':     ['slate','steel blue','charcoal'],
    'silver':     ['slate','steel blue','charcoal'],
    'ash':        ['charcoal','stone','slate'],
    'graphite':   ['charcoal','slate','black'],
    'smoke':      ['charcoal','slate','stone'],
    'cement':     ['stone','charcoal','slate'],
    'granite':    ['charcoal','stone','slate'],
    'limestone':  ['stone','cream','off-white'],

    // ── Blues ──
    'dark blue':  ['navy','midnight','steel blue'],
    'light blue': ['sky blue','steel blue','slate'],
    'baby blue':  ['sky blue'],
    'royal blue': ['navy','steel blue'],
    'teal':       ['steel blue','slate','navy'],
    'indigo':     ['navy','midnight'],
    'cobalt':     ['navy','steel blue'],
    'denim':      ['navy','steel blue','slate'],
    'marine':     ['navy','steel blue'],

    // ── Greens ──
    'dark green':  ['forest','moss','olive'],
    'light green': ['sage','sage green'],
    'olive':       ['olive','moss','forest'],
    'sage':        ['sage','sage green','olive'],
    'mint':        ['sage','sage green'],
    'emerald':     ['green','forest'],
    'hunter':      ['forest','moss','olive'],
    'army':        ['olive','moss','forest'],
    'military':    ['olive','moss','forest','khaki'],
    'camo':        ['olive','moss','forest','khaki'],
    'pine':        ['forest','moss','olive'],
    'fern':        ['sage','sage green','olive','moss'],
    'moss':        ['moss','olive','forest','sage'],

    // ── Reds / Wines ──
    'wine':       ['burgundy'],
    'maroon':     ['burgundy','dark brown'],
    'crimson':    ['burgundy','coral'],
    'berry':      ['burgundy','coral'],
    'brick':      ['burgundy','clay','coral'],
    'oxblood':    ['burgundy','dark brown'],
    'plum':       ['burgundy'],

    // ── Kids collection color terms ──
    'lavender':       ['lavender','purple'],
    'rose':           ['rose','pink','coral','dusty rose'],
    'dusty rose':     ['dusty rose','rose','pink'],
    'sunshine':       ['sunshine yellow','yellow','amber'],
    'sunshine yellow':['sunshine yellow','yellow','amber'],
    'ocean':          ['ocean blue','ocean','sky blue','navy'],
    'ocean blue':     ['ocean blue','ocean','sky blue','navy'],
    'forest green':   ['forest green','forest','moss','olive'],
    'sky blue':       ['sky blue','ocean blue','ocean','steel blue'],
    'steel blue':     ['steel blue','navy','slate','sky blue'],
    'dusty':          ['dusty rose','rose','sand'],
    'mint':           ['mint','sage','sage green'],
    'tan':            ['tan','sand','khaki','stone'],
    'rust':           ['rust','amber','orange'],

    // ── Brand color names (so direct searches work too) ──
    'charcoal':   ['charcoal','slate'],
    'navy':       ['navy','midnight','steel blue'],
    'stone':      ['stone','sand','cream','khaki'],
    'slate':      ['slate','charcoal','steel blue'],
    'amber':      ['amber','sand','coral'],
    'walnut':     ['walnut','dark brown'],
    'coral':      ['coral','amber'],
    'clay':       ['clay','coral','amber','walnut'],
    'moss':       ['moss','olive','forest','sage'],
    'forest':     ['forest','moss','olive'],
    'midnight':   ['midnight','navy','black'],

    // ── Mood / style terms ──
    'dark':       ['charcoal','black','midnight','dark brown','navy'],
    'light':      ['cream','off-white','white','sand','sky blue'],
    'bright':     ['coral','sky blue','amber','white'],
    'muted':      ['stone','charcoal','slate','olive','sage'],
    'neutral':    ['stone','sand','cream','khaki','off-white','charcoal','black','white'],
    'warm':       ['amber','sand','coral','walnut','clay','cream','khaki'],
    'cool':       ['navy','slate','steel blue','charcoal','sky blue']
  };

  // Get the first available image URL for a product
  function getProductImageUrl(productId) {
    var p = PRODUCTS[productId];
    if (!p || !p.imageMap) return '';
    var firstColor = p.colors[0].toLowerCase();
    var filename = p.imageMap[firstColor];
    if (!filename) {
      // Fallback: grab first value from imageMap
      var keys = Object.keys(p.imageMap);
      if (keys.length > 0) filename = p.imageMap[keys[0]];
    }
    return filename ? CDN_BASE + filename + '.jpg' : '';
  }

  // Get image URL for a specific color
  // Reuse the main COLOR_ALIASES defined above (line ~207) — no duplicate needed

  function getProductImageForColor(productId, colorName) {
    var p = PRODUCTS[productId];
    if (!p || !p.imageMap) return getProductImageUrl(productId);
    var key = colorName.toLowerCase();

    // Direct match
    if (p.imageMap[key]) return CDN_BASE + p.imageMap[key] + '.jpg';

    // Try aliases — find the first alias that exists in this product's imageMap
    var aliases = COLOR_ALIASES[key];
    if (aliases) {
      for (var ai = 0; ai < aliases.length; ai++) {
        if (p.imageMap[aliases[ai]]) return CDN_BASE + p.imageMap[aliases[ai]] + '.jpg';
      }
    }

    // Try partial match — "steel blue" should match "steel blue" key, "blue" should find "sky blue"
    var mapKeys = Object.keys(p.imageMap);
    for (var mi = 0; mi < mapKeys.length; mi++) {
      if (mapKeys[mi].indexOf(key) !== -1 || key.indexOf(mapKeys[mi]) !== -1) {
        return CDN_BASE + p.imageMap[mapKeys[mi]] + '.jpg';
      }
    }

    return getProductImageUrl(productId);
  }

  // ─── STYLES ─────────────────────────────────────────────────────────────────
  var styles = document.createElement('style');
  styles.textContent = [
    /* Agent button */
    '#rb-agent-btn{position:fixed;bottom:24px;right:24px;z-index:9999;width:56px;height:56px;border-radius:50%;',
    'background:#1a1a1e;border:2px solid rgba(139,155,168,0.3);cursor:pointer;display:flex;align-items:center;',
    'justify-content:center;box-shadow:0 4px 24px rgba(0,0,0,0.3);transition:all 0.3s ease;}',
    '#rb-agent-btn:hover{transform:scale(1.08);border-color:rgba(181,90,48,0.6);box-shadow:0 6px 32px rgba(0,0,0,0.4);}',
    '#rb-agent-btn .pulse{position:absolute;width:56px;height:56px;border-radius:50%;border:2px solid rgba(181,90,48,0.4);animation:rb-pulse 2s ease-in-out infinite;}',
    '@keyframes rb-pulse{0%,100%{transform:scale(1);opacity:1;}50%{transform:scale(1.3);opacity:0;}}',
    '#rb-agent-btn svg{width:24px;height:24px;fill:none;stroke:#8b9ba8;stroke-width:2;stroke-linecap:round;stroke-linejoin:round;}',
    '#rb-agent-btn.active svg{stroke:#b55a30;}',
    '#rb-agent-btn.active{border-color:rgba(181,90,48,0.6);}',

    /* Status indicator */
    '#rb-agent-status{position:fixed;bottom:88px;right:24px;z-index:9998;background:#1a1a1e;color:#8b9ba8;',
    'font-family:"Inter",sans-serif;font-size:12px;letter-spacing:0.05em;padding:8px 16px;border-radius:20px;',
    'border:1px solid rgba(139,155,168,0.2);opacity:0;transform:translateY(8px);transition:all 0.3s ease;pointer-events:none;}',
    '#rb-agent-status.visible{opacity:1;transform:translateY(0);}',
    '#rb-agent-status.listening{color:#b55a30;border-color:rgba(181,90,48,0.3);}',

    /* Results overlay — z-index jumps to 99997 when open to sit above ElevenLabs widget shadow DOM */
    '#rb-results-overlay{position:fixed;top:0;right:-480px;width:480px;height:100vh;z-index:9997;',
    'background:#111114;border-left:1px solid rgba(139,155,168,0.15);overflow-y:auto;transition:right 0.4s cubic-bezier(0.16,1,0.3,1);',
    'font-family:"Inter",sans-serif;-webkit-overflow-scrolling:touch;}',
    '#rb-results-overlay.open{right:0;z-index:99997;}',
    '#rb-results-header{display:flex;align-items:center;justify-content:space-between;padding:24px;border-bottom:1px solid rgba(139,155,168,0.1);}',
    '#rb-results-header h3{color:#e8e3db;font-size:15px;font-weight:500;letter-spacing:0.05em;margin:0;}',
    '#rb-results-close{background:none;border:none;color:#8b9ba8;font-size:20px;cursor:pointer;padding:4px 8px;}',
    '#rb-results-close:hover{color:#e8e3db;}',
    '#rb-results-body{padding:16px;}',

    /* Wide mode for iframe PDP — expands overlay to ~90vw */
    '#rb-results-overlay.rb-wide{width:90vw;right:-90vw;}',
    '#rb-results-overlay.rb-wide.open{right:0;}',
    '#rb-results-overlay.rb-wide #rb-results-body{padding:0;height:calc(100vh - 72px);overflow:hidden;}',

    /* Product cards — entire card is clickable via inline onclick */
    '.rb-product-card{display:block;background:#1a1a1e;border:1px solid rgba(139,155,168,0.12);border-radius:4px;margin-bottom:16px;',
    'cursor:pointer;transition:border-color 0.2s;}',
    '.rb-product-card:hover{border-color:rgba(181,90,48,0.4);}',
    '.rb-product-card-inner{display:flex;gap:0;}',
    '.rb-product-img-wrap{width:140px;min-width:140px;height:160px;overflow:hidden;position:relative;flex-shrink:0;}',
    '.rb-product-img-wrap img{width:100%;height:100%;object-fit:cover;display:block;transition:transform 0.3s ease;}',
    '.rb-product-card:hover .rb-product-img-wrap img{transform:scale(1.05);}',
    '.rb-product-info{padding:16px;display:flex;flex-direction:column;justify-content:center;flex:1;min-width:0;}',
    '.rb-product-name{color:#e8e3db;font-size:14px;font-weight:500;letter-spacing:0.03em;margin:0 0 4px;}',
    '.rb-product-meta{color:#8b9ba8;font-size:12px;letter-spacing:0.03em;margin:0 0 8px;}',
    '.rb-product-price{color:#b55a30;font-weight:500;}',
    '.rb-product-colors{display:flex;gap:6px;flex-wrap:wrap;}',
    '.rb-color-dot{width:14px;height:14px;border-radius:50%;border:1px solid rgba(139,155,168,0.3);}',

    /* Hover-to-enlarge: cloned image rendered on document.body, outside the overlay */
    '#rb-zoom-clone{position:fixed;z-index:99998;pointer-events:none;opacity:0;transition:opacity 0.15s ease;',
    'background:#111114;border:1px solid rgba(181,90,48,0.4);border-radius:6px;padding:6px;',
    'box-shadow:0 12px 48px rgba(0,0,0,0.7);}',
    '#rb-zoom-clone.visible{opacity:1;}',
    '#rb-zoom-clone img{display:block;width:340px;height:400px;object-fit:cover;border-radius:4px;}',
    '#rb-zoom-clone .rb-zoom-label{color:#e8e3db;font-family:"Inter",sans-serif;text-align:center;',
    'font-size:13px;font-weight:500;letter-spacing:0.04em;padding:8px 4px 4px;}',
    '#rb-zoom-clone .rb-zoom-label span{color:#b55a30;margin-left:6px;}',

    /* Lightbox (mobile tap-to-enlarge fallback) */
    '#rb-lightbox{position:fixed;inset:0;z-index:99999;background:rgba(0,0,0,0.85);display:none;align-items:center;justify-content:center;cursor:zoom-out;}',
    '#rb-lightbox.visible{display:flex;}',
    '#rb-lightbox img{max-width:85vw;max-height:85vh;object-fit:contain;border-radius:4px;box-shadow:0 8px 48px rgba(0,0,0,0.6);}',
    '#rb-lightbox-info{position:absolute;bottom:40px;left:50%;transform:translateX(-50%);color:#e8e3db;font-family:"Inter",sans-serif;text-align:center;',
    'font-size:16px;font-weight:500;letter-spacing:0.05em;background:rgba(17,17,20,0.8);padding:12px 24px;border-radius:4px;}',
    '#rb-lightbox-info span{color:#b55a30;font-size:14px;display:block;margin-top:4px;font-weight:400;}',

    /* Overlay backdrop — z-index jumps to 99996 when visible to sit above ElevenLabs widget */
    '#rb-overlay-backdrop{position:fixed;inset:0;z-index:9996;background:rgba(0,0,0,0.5);opacity:0;pointer-events:none;transition:opacity 0.3s;}',
    '#rb-overlay-backdrop.visible{opacity:1;pointer-events:auto;z-index:99996;}',

    /* ElevenLabs widget: hidden until activated, then positioned above our mic button */
    'elevenlabs-convai{position:fixed !important;bottom:-9999px !important;right:24px !important;z-index:9998 !important;opacity:0 !important;pointer-events:none !important;transition:opacity 0.3s ease,bottom 0.3s ease !important;}',
    'elevenlabs-convai.rb-show{bottom:90px !important;opacity:1 !important;pointer-events:auto !important;}',

    /* Pulse animation for mic button when resuming after navigation */
    '@keyframes rb-resume-pulse{0%{box-shadow:0 0 0 0 rgba(181,90,48,0.6);}70%{box-shadow:0 0 0 16px rgba(181,90,48,0);}100%{box-shadow:0 0 0 0 rgba(181,90,48,0);}}',
    '#rb-agent-btn.rb-pulse-resume{animation:rb-resume-pulse 1.2s ease-out infinite;}',

    /* Mobile responsive */
    '@media(max-width:768px){',
    '  #rb-results-overlay{width:100%;right:-100%;}',
    '  #rb-results-overlay.rb-wide{width:100%;right:-100%;}',
    '  #rb-results-overlay.rb-wide.open{right:0;}',
    '  #rb-results-overlay.rb-wide #rb-results-body{height:calc(100vh - 72px);height:calc(100dvh - 72px);}',
    '  #rb-agent-btn{bottom:16px;right:16px;width:48px;height:48px;}',
    '  #rb-agent-btn .pulse{width:48px;height:48px;}',
    '  #rb-agent-status{bottom:72px;right:16px;}',
    '  .rb-product-img-wrap{width:100px;min-width:100px;height:120px;}',
    '  #rb-zoom-clone{display:none !important;}',
    '  .rb-pdp-grid{grid-template-columns:1fr !important;min-height:auto !important;}',
    '  .rb-pdp-gallery-col{padding:24px 24px 16px !important;}',
    '  .rb-pdp-info-col{padding:0 24px 80px !important;}',
    '}',

    /* Agent toast — guaranteed to render above everything */
    '#rb-agent-toast{position:fixed;bottom:0;left:0;right:0;z-index:2147483647;background:#1a1f25;color:#fff;',
    'font-family:"Inter",sans-serif;font-size:14px;letter-spacing:0.03em;padding:16px 24px;',
    'text-align:center;transform:translateY(100%);transition:transform 0.35s cubic-bezier(0.16,1,0.3,1);',
    'box-shadow:0 -2px 16px rgba(0,0,0,0.3);pointer-events:none;}',
    '#rb-agent-toast.show{transform:translateY(0);}',
    '#rb-agent-toast .rb-toast-icon{display:inline-block;margin-right:8px;font-size:16px;}'
  ].join('\n');
  document.head.appendChild(styles);

  // ─── DOM ELEMENTS ──────────────────────────────────────────────────────────

  // Agent button
  var btn = document.createElement('div');
  btn.id = 'rb-agent-btn';
  btn.innerHTML = '<div class="pulse"></div><svg viewBox="0 0 24 24"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>';
  btn.title = 'Rainburg Shopping Assistant';

  // Status bubble
  var status = document.createElement('div');
  status.id = 'rb-agent-status';
  status.textContent = 'Assistant ready';

  // Results overlay
  var overlay = document.createElement('div');
  overlay.id = 'rb-results-overlay';
  overlay.innerHTML = '<div id="rb-results-header"><h3>Results</h3><button id="rb-results-close">&times;</button></div><div id="rb-results-body"></div>';

  // Backdrop
  var backdrop = document.createElement('div');
  backdrop.id = 'rb-overlay-backdrop';

  // Lightbox for mobile tap-to-enlarge
  var lightbox = document.createElement('div');
  lightbox.id = 'rb-lightbox';
  lightbox.innerHTML = '<img src="" alt=""><div id="rb-lightbox-info"><span></span></div>';

  // Zoom clone — rendered on body, completely outside the overlay DOM
  var zoomClone = document.createElement('div');
  zoomClone.id = 'rb-zoom-clone';
  zoomClone.innerHTML = '<img src="" alt=""><div class="rb-zoom-label"><span></span></div>';

  // Agent toast — full-width bottom notification
  var agentToast = document.createElement('div');
  agentToast.id = 'rb-agent-toast';

  document.body.appendChild(btn);
  document.body.appendChild(status);
  document.body.appendChild(overlay);
  document.body.appendChild(backdrop);
  document.body.appendChild(lightbox);
  document.body.appendChild(zoomClone);
  document.body.appendChild(agentToast);

  var toastTimer = null;
  var toastQueue = [];
  var toastShowing = false;

  function showAgentToast(msg, icon, duration) {
    toastQueue.push({ msg: msg, icon: icon || '', duration: duration || 2500 });
    if (!toastShowing) processToastQueue();
  }

  function processToastQueue() {
    if (toastQueue.length === 0) {
      toastShowing = false;
      return;
    }
    toastShowing = true;
    var item = toastQueue.shift();
    agentToast.innerHTML = (item.icon ? '<span class="rb-toast-icon">' + item.icon + '</span>' : '') + item.msg;
    agentToast.classList.add('show');
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      agentToast.classList.remove('show');
      // Small gap before showing next toast
      setTimeout(processToastQueue, 400);
    }, item.duration);
  }

  // ─── LIGHTBOX (mobile fallback) ──────────────────────────────────────────────
  lightbox.addEventListener('click', function () {
    lightbox.classList.remove('visible');
  });

  function openLightbox(imgSrc, productName, price) {
    var img = lightbox.querySelector('img');
    var info = document.getElementById('rb-lightbox-info');
    img.src = imgSrc;
    img.alt = productName;
    info.innerHTML = productName + '<span>&euro;' + price + '</span>';
    lightbox.classList.add('visible');
  }

  // ─── CARD INTERACTION (hover + click) ──────────────────────────────────────
  // Listeners are attached DIRECTLY to each card after rendering (not delegation).
  // This avoids all event propagation / stacking-context issues with the overlay.

  function attachCardListeners() {
    var cards = document.querySelectorAll('#rb-results-body .rb-product-card');
    console.log('[Rainburg Agent] Attaching listeners to', cards.length, 'cards');

    for (var i = 0; i < cards.length; i++) {
      (function (card) {
        // ── CLICK: show product detail view in overlay (preserves voice session) ──
        card.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          var productId = card.getAttribute('data-product-id');
          console.log('[Rainburg Agent] Card clicked:', card.getAttribute('data-name'), '→ detail view');
          if (productId) {
            zoomClone.classList.remove('visible');
            actionShowProduct({ productId: productId });
          }
        });

        // ── HOVER: show zoom clone when mouse enters image area ──
        var imgWrap = card.querySelector('.rb-product-img-wrap');
        if (imgWrap) {
          imgWrap.addEventListener('mouseenter', function () {
            var imgSrc = card.getAttribute('data-img');
            var name = card.getAttribute('data-name');
            var price = card.getAttribute('data-price');
            console.log('[Rainburg Agent] Hover on:', name);
            if (!imgSrc) return;

            var rect = imgWrap.getBoundingClientRect();
            var img = zoomClone.querySelector('img');
            var label = zoomClone.querySelector('.rb-zoom-label');
            img.src = imgSrc;
            label.innerHTML = name + '<span>&euro;' + price + '</span>';

            var cloneW = 352;
            var cloneH = 450;
            var left = rect.left - cloneW - 12;
            if (left < 8) left = 8;
            var top = rect.top + (rect.height / 2) - (cloneH / 2);
            if (top < 8) top = 8;
            if (top + cloneH > window.innerHeight - 8) top = window.innerHeight - cloneH - 8;

            zoomClone.style.left = left + 'px';
            zoomClone.style.top = top + 'px';
            zoomClone.classList.add('visible');
          });

          imgWrap.addEventListener('mouseleave', function () {
            zoomClone.classList.remove('visible');
          });
        }
      })(cards[i]);
    }
  }

  // ─── ELEVENLABS WIDGET ─────────────────────────────────────────────────────
  var widgetLoaded = false;
  var agentActive = false;
  var elevenLabsEl = null;

  function loadElevenLabsWidget() {
    if (widgetLoaded) return;
    widgetLoaded = true;

    elevenLabsEl = document.createElement('elevenlabs-convai');
    elevenLabsEl.setAttribute('agent-id', ELEVENLABS_AGENT_ID);
    document.body.appendChild(elevenLabsEl);

    var sc = document.createElement('script');
    sc.src = 'https://unpkg.com/@elevenlabs/convai-widget-embed';
    sc.async = true;
    sc.onload = function () {
      // Show the widget after it loads so user can click "Start call"
      setTimeout(function () {
        if (elevenLabsEl) elevenLabsEl.classList.add('rb-show');
      }, 300);
    };
    document.body.appendChild(sc);
  }

  // Try to find and click the "Start call" button inside the ElevenLabs shadow DOM.
  // This turns our mic button into a true one-click call starter.
  function triggerElevenLabsCall() {
    if (!elevenLabsEl) return false;

    // Try shadow DOM first
    var root = elevenLabsEl.shadowRoot;
    if (root) {
      // Look for buttons with common call-start text/labels
      var buttons = root.querySelectorAll('button');
      for (var i = 0; i < buttons.length; i++) {
        var text = (buttons[i].textContent || '').toLowerCase().trim();
        var ariaLabel = (buttons[i].getAttribute('aria-label') || '').toLowerCase();
        if (text.indexOf('start') !== -1 || text.indexOf('call') !== -1 ||
            ariaLabel.indexOf('start') !== -1 || ariaLabel.indexOf('call') !== -1 ||
            text === '' /* icon-only button, likely the call orb */) {
          console.log('[Rainburg Agent] Found ElevenLabs call button:', buttons[i].tagName, text || '(icon)');
          buttons[i].click();
          return true;
        }
      }
      // Fallback: click any large circular element (the orb)
      var allEls = root.querySelectorAll('div, button');
      for (var j = 0; j < allEls.length; j++) {
        var cs = window.getComputedStyle(allEls[j]);
        if (cs.borderRadius && parseInt(cs.borderRadius) >= 20 && cs.cursor === 'pointer') {
          console.log('[Rainburg Agent] Found ElevenLabs orb element, clicking');
          allEls[j].click();
          return true;
        }
      }
    }
    // Shadow DOM not accessible — try clicking the custom element itself
    console.log('[Rainburg Agent] Shadow DOM not accessible, clicking widget element');
    elevenLabsEl.click();
    return false;
  }

  btn.addEventListener('click', function () {
    agentActive = !agentActive;
    btn.classList.toggle('active', agentActive);
    saveCallState();

    if (agentActive) {
      loadElevenLabsWidget();
      if (elevenLabsEl) elevenLabsEl.classList.add('rb-show');
      connectWebSocket();

      // Try to auto-start the call after widget has time to render
      setTimeout(function () {
        var started = triggerElevenLabsCall();
        if (!started) {
          showStatus('Tap the voice assistant above to start', 'listening');
        }
      }, 1200);
    } else {
      if (elevenLabsEl) elevenLabsEl.classList.remove('rb-show');
      showStatus('Assistant paused', '');
      setTimeout(function () { hideStatus(); }, 2000);
    }
  });

  // ─── AUTO-RECONNECT AFTER PAGE NAVIGATION ──────────────────────────────────
  // If the user was actively using the voice agent before navigating to this page,
  // auto-load the widget, connect WebSocket, and show a "ready to resume" toast.
  if (wasInCall()) {
    console.log('[Rainburg Agent] Detected previous active session — auto-reconnecting');
    agentActive = true;
    btn.classList.add('active');

    // Auto-load widget and connect
    loadElevenLabsWidget();
    connectWebSocket();

    // Pulse the mic button to draw attention
    btn.classList.add('rb-pulse-resume');
    setTimeout(function () { btn.classList.remove('rb-pulse-resume'); }, 8000);

    // Show toast with longer duration so user can read it (5 seconds)
    setTimeout(function () {
      showAgentToast('Voice assistant ready \u2014 click Start Call to resume', '\uD83C\uDF99\uFE0F', 5000);
    }, 1000);
  }

  // Save call state before any navigation so the next page can auto-reconnect
  window.addEventListener('beforeunload', function () {
    saveCallState();
    saveCart();
  });

  // ─── STATUS BUBBLE ─────────────────────────────────────────────────────────
  function showStatus(text, className) {
    status.textContent = text;
    status.className = 'visible' + (className ? ' ' + className : '');
  }

  function hideStatus() {
    status.className = '';
  }

  // ─── WEBSOCKET CONNECTION ──────────────────────────────────────────────────
  var ws = null;
  var wsReconnectTimer = null;

  // Helper: build cart summary with pre-calculated total (so Claude never does math)
  // Defined at module scope so actionCartAdd/actionCartRemove can access it
  function getCartSummary() {
    var items = (window.cart || []).map(function (item) {
      return { name: item.name, color: item.color, size: item.size, price: item.eurPrice };
    });
    var total = 0;
    for (var i = 0; i < items.length; i++) {
      total += parseFloat(items[i].price) || 0;
    }
    return { items: items, count: items.length, total: total };
  }

  function connectWebSocket() {
    if (ws && ws.readyState <= 1) return;

    var url = WS_BASE_URL + '/ws/actions?session=' + encodeURIComponent(sessionId);
    var socket;
    try {
      socket = new WebSocket(url);
      ws = socket;
    } catch (e) {
      console.warn('[Rainburg Agent] WebSocket connection failed:', e.message);
      return;
    }

    socket.onopen = function () {
      console.log('[Rainburg Agent] WebSocket connected, session:', sessionId);
      var cs = getCartSummary();
      socket.send(JSON.stringify({
        type: 'handshake',
        sessionId: sessionId,
        currentPage: window.location.pathname,
        userAgent: navigator.userAgent,
        cartItemCount: cs.count,
        cartItems: cs.items,
        cartTotal: cs.total,
        cartPanelOpen: cartPanelOpen
      }));
    };

    socket.onmessage = function (event) {
      try {
        var action = JSON.parse(event.data);
        console.log('[Rainburg Agent] Action received:', action.type, JSON.stringify(action));
        executeAction(action);
      } catch (e) {
        console.warn('[Rainburg Agent] Invalid action:', event.data, e.message);
      }
    };

    socket.onclose = function () {
      console.log('[Rainburg Agent] WebSocket closed, agentActive:', agentActive);
      if (agentActive) {
        console.log('[Rainburg Agent] Reconnecting in 3s...');
        wsReconnectTimer = setTimeout(connectWebSocket, 3000);
      }
    };

    socket.onerror = function (e) {
      console.warn('[Rainburg Agent] WebSocket error');
    };
  }

  // ─── ACTION BRIDGE ─────────────────────────────────────────────────────────

  function executeAction(action) {
    console.log('[Rainburg Agent] executeAction:', action.type, 'overlay open?', overlay.classList.contains('open'));
    // Dismiss hover preview on any voice action (prevents stuck zoom clone
    // when user is hovering and voice command changes/removes the hovered card)
    zoomClone.classList.remove('visible');
    switch (action.type) {
      case 'navigate':    actionNavigate(action); break;
      case 'filter':      actionFilter(action); break;
      case 'showProduct': actionShowProduct(action); break;
      case 'goToProduct':  actionGoToProduct(action); break;
      case 'closeOverlay': closeOverlay(); break;
      case 'scrollTo':    actionScrollTo(action); break;
      case 'cartAdd':     actionCartAdd(action); break;
      case 'cartView':    actionCartView(action); break;
      case 'cartClose':   actionCartClose(); break;
      case 'cartRemove':  actionCartRemove(action); break;
      case 'navigatePage': actionNavigatePage(action); break;
      case 'checkout':    actionCheckout(action); break;
      case 'checkoutStep':
        if (typeof window.rbCheckoutStep === 'function') window.rbCheckoutStep(action.step || 2);
        break;
      case 'fillShipping':
        if (action.firstName) { var f = document.getElementById('rb-ship-fname'); if (f) f.value = action.firstName; }
        if (action.lastName)  { var l = document.getElementById('rb-ship-lname'); if (l) l.value = action.lastName; }
        if (action.email)     { var e = document.getElementById('rb-ship-email'); if (e) e.value = action.email; }
        if (action.address)   { var a = document.getElementById('rb-ship-addr');  if (a) a.value = action.address; }
        if (action.city)      { var c = document.getElementById('rb-ship-city');  if (c) c.value = action.city; }
        if (action.zip)       { var z = document.getElementById('rb-ship-zip');   if (z) z.value = action.zip; }
        if (action.country)   { var co = document.getElementById('rb-ship-country'); if (co) co.value = action.country; }
        showStatus('Shipping details updated', '');
        setTimeout(hideStatus, 3000);
        break;
      case 'status':
        showStatus(action.text || '', action.className || '');
        if (!action.persist) setTimeout(hideStatus, 4000);
        break;
      default:
        console.warn('[Rainburg Agent] Unknown action type:', action.type);
    }
  }

  // ── Navigate to a page (full page navigation — saves cart first) ──
  function actionNavigate(action) {
    if (action.url) {
      showStatus('Taking you there...', '');
      // Save cart to sessionStorage before navigating so it persists
      saveCart();
      // Append ?agent=1 to preserve activation on the new page
      var url = action.url;
      if (url.indexOf('agent=') === -1) {
        url += (url.indexOf('?') !== -1 ? '&' : '?') + 'agent=1';
      }
      setTimeout(function () {
        window.location.href = url;
      }, 500);
    }
  }

  // ── Filter products and show results overlay ──
  function actionFilter(action) {
    // Reset wide mode from any previous goToProduct or navigatePage view
    overlay.classList.remove('rb-wide');

    var filters = action.filters || {};
    var results = [];

    // ── Also trigger native page filters on the kids/teens page ──
    var isKidsPage = window.location.pathname.indexOf('/pages/kids') !== -1;
    var isTeensPage = window.location.pathname.indexOf('/pages/teens') !== -1;
    if ((isKidsPage || isTeensPage) && typeof window.renderProducts === 'function') {
      // Map voice agent category names to page category values
      var catMap = {'jacket':'jackets','pants':'pants','tops':'tops','t-shirt':'tops','hoodie':'tops',
                    'fleece':'tops','vest':'jackets','boots':'boots','cap':'accessories','shorts':'shorts',
                    'socks':'accessories','beanie':'accessories','accessories':'accessories'};
      if (filters.category) {
        window.filteredCategory = catMap[filters.category.toLowerCase()] || filters.category.toLowerCase();
      } else {
        window.filteredCategory = 'all';
      }
      // On kids/teens page, season filter maps to sub-seasons (spring/summer/autumn/winter)
      // but voice agent sends season:"kids"/"teens" — so reset to "all" on the page
      if (filters.season === 'kids' || filters.season === 'teens' || !filters.season) {
        window.filteredSeason = 'all';
      } else {
        window.filteredSeason = filters.season.toLowerCase();
      }
      window.renderProducts();
      // Scroll to products grid
      var grid = document.getElementById('productsGrid');
      if (grid) grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    Object.keys(PRODUCTS).forEach(function (id) {
      var p = PRODUCTS[id];
      var match = true;

      if (filters.season) {
        var fs = filters.season.toLowerCase();
        if (fs === 'adults' || fs === 'adult') {
          // "adults" means exclude kids and teens
          if (p.season === 'kids' || p.season === 'teens') match = false;
        } else {
          if (p.season !== fs) match = false;
        }
      }

      if (filters.category) {
        var cat = filters.category.toLowerCase();
        if (cat === 'trousers') cat = 'pants';
        // "hat" should match caps, beanies, and any headwear
        var isHatQuery = (cat === 'hat' || cat === 'hats' || cat === 'headwear');
        var hatCategories = ['cap', 'beanie', 'hat'];
        if (isHatQuery) {
          if (hatCategories.indexOf(p.category) === -1) match = false;
        } else {
          if (p.category !== cat) match = false;
        }
      }

      if (filters.priceMax && p.price > filters.priceMax) match = false;
      if (filters.priceMin && p.price < filters.priceMin) match = false;

      if (filters.color && match) {
        var targetColor = filters.color.toLowerCase();
        var colorMatch = false;
        var matchedBrandColor = null; // Track which brand color actually matched (for image lookup)
        var productColors = p.colors.map(function (c) { return c.toLowerCase(); });

        // Direct match: check if consumer term matches any product color
        productColors.forEach(function (c) {
          if (c.indexOf(targetColor) !== -1 || targetColor.indexOf(c) !== -1) {
            colorMatch = true;
            matchedBrandColor = c;
          }
        });

        // Alias match: expand consumer term to brand color names
        if (!colorMatch && COLOR_ALIASES[targetColor]) {
          COLOR_ALIASES[targetColor].forEach(function (alias) {
            if (colorMatch) return; // Already found a match
            productColors.forEach(function (c) {
              if (!colorMatch && (c.indexOf(alias) !== -1 || alias.indexOf(c) !== -1)) {
                colorMatch = true;
                matchedBrandColor = c;
              }
            });
          });
        }

        if (!colorMatch) match = false;
      }

      if (match) {
        // Pass matchedBrandColor so the image can show the correct variant
        var resolvedColor = matchedBrandColor || (filters.color ? filters.color.toLowerCase() : null);
        results.push({ id: id, product: p, matchedColor: resolvedColor });
      }
    });

    // Sort results if requested
    if (filters.sort) {
      var sortKey = filters.sort.toLowerCase();
      if (sortKey === 'price_asc' || sortKey === 'price' || sortKey === 'cheapest') {
        results.sort(function (a, b) { return a.product.price - b.product.price; });
      } else if (sortKey === 'price_desc' || sortKey === 'expensive') {
        results.sort(function (a, b) { return b.product.price - a.product.price; });
      } else if (sortKey === 'name' || sortKey === 'name_asc') {
        results.sort(function (a, b) { return a.product.name.localeCompare(b.product.name); });
      }
      console.log('[Rainburg Agent] Sorted by:', sortKey);
    }

    // Limit results if requested (e.g. "top 3 recommendations")
    if (filters.limit && filters.limit > 0 && results.length > filters.limit) {
      console.log('[Rainburg Agent] Limiting results from', results.length, 'to', filters.limit);
      results = results.slice(0, filters.limit);
    }

    renderResults(results, filters);
  }

  // ── Show a specific product (detail view in overlay — no page navigation) ──
  function actionShowProduct(action) {
    var p = PRODUCTS[action.productId];
    if (!p) return;

    // Reset wide mode from any previous goToProduct or navigatePage view
    overlay.classList.remove('rb-wide');

    var body = document.getElementById('rb-results-body');
    var header = overlay.querySelector('h3');
    header.textContent = p.name;

    // Default to first color, or matched color
    var activeColor = (action.color || p.colors[0]).toLowerCase();
    var imgUrl = getProductImageForColor(action.productId, activeColor);

    // Build color option buttons
    var colorBtns = p.colors.map(function (c) {
      var hex = COLOR_HEX[c.toLowerCase()] || '#888';
      var isActive = c.toLowerCase() === activeColor;
      var border = isActive ? '3px solid #b55a30' : '2px solid rgba(139,155,168,0.3)';
      return '<div class="rb-detail-color" data-color="' + c.toLowerCase() + '" ' +
        'style="width:28px;height:28px;border-radius:50%;background:' + hex + ';border:' + border +
        ';cursor:pointer;transition:border 0.2s;" title="' + c + '"></div>';
    }).join('');

    body.innerHTML =
      '<div class="rb-product-detail" data-product-id="' + action.productId + '">' +
        '<div style="position:relative;width:100%;max-width:420px;margin:0 auto 16px;">' +
          '<img id="rb-detail-img" src="' + imgUrl + '" alt="' + p.name + '" ' +
            'style="width:100%;height:auto;max-height:480px;object-fit:cover;border-radius:6px;display:block;">' +
        '</div>' +
        '<div style="padding:0 8px;">' +
          '<h2 style="color:#e8e3db;font-size:20px;font-weight:500;margin:0 0 8px;letter-spacing:0.03em;">' + p.name + '</h2>' +
          '<p style="color:#b55a30;font-size:18px;font-weight:500;margin:0 0 16px;">&euro;' + p.price + '</p>' +
          '<p style="color:#8b9ba8;font-size:13px;margin:0 0 12px;">' +
            p.season.charAt(0).toUpperCase() + p.season.slice(1) + ' 2026 &middot; ' + p.category.charAt(0).toUpperCase() + p.category.slice(1) +
          '</p>' +
          '<div style="display:flex;gap:10px;flex-wrap:wrap;margin-bottom:20px;">' + colorBtns + '</div>' +
          '<a href="' + p.page + '#products" target="_blank" rel="noopener" ' +
            'style="display:inline-block;padding:12px 24px;background:#b55a30;color:#fff;text-decoration:none;' +
            'border-radius:4px;font-size:13px;font-weight:500;letter-spacing:0.05em;transition:background 0.2s;">' +
            'View on page &rarr;</a>' +
        '</div>' +
      '</div>';

    // Attach color-switch listeners
    var colorDots = body.querySelectorAll('.rb-detail-color');
    var detailImg = document.getElementById('rb-detail-img');
    for (var i = 0; i < colorDots.length; i++) {
      (function (dot) {
        dot.addEventListener('click', function (e) {
          e.stopPropagation();
          var c = dot.getAttribute('data-color');
          detailImg.src = getProductImageForColor(action.productId, c);
          // Update active border
          for (var j = 0; j < colorDots.length; j++) {
            colorDots[j].style.border = '2px solid rgba(139,155,168,0.3)';
          }
          dot.style.border = '3px solid #b55a30';
        });
      })(colorDots[i]);
    }

    openOverlay();
    showStatus(p.name + ' — tap a color to preview', '');
    setTimeout(hideStatus, 4000);
  }

  // ── Map agent product IDs → seasonal template product names ──
  var PRODUCT_NAME_MAP = {
    'windbreaker':      'Breeze Windbreaker',
    'rain-jacket':      'Nimbus Rain Jacket',
    'trail-pants':      'Trail Light Pants',
    'softshell':        'Vernal Soft Shell Jacket',
    'soleil':           'The Soleil',
    'linen-jacket':     'The Linen Jacket',
    'tramontane':       'The Tramontane',
    'zephyr-vest':      'The Zephyr Vest',
    'torrent':          'The Torrent',
    'ember':            'The Ember',
    'gale':             'The Gale',
    'ridge-fleece':     'The Ridge Fleece',
    'shell-jacket':     'Alpine Pro Shell Jacket',
    'insulated-jacket': 'Summit Insulated Jacket',
    'alpine-pants':     'Alpine Pro Ski Pants',
    'summit-pants':     'Summit Ski Pants',
    'strand-sock':      'The Strand Sock',
    'harbor-tee':       'The Harbor Tee',
    'dock-cap':         'The Dock Cap',
    'fjord-glove-m':    'The Fjord Glove (Men)',
    'fjord-glove-w':    'The Fjord Glove (Women)',
    'nordic-beanie-m':  'The Nordic Beanie (Men)',
    'nordic-beanie-w':  'The Nordic Beanie (Women)',
    'strom-crewneck':   'The Strom Crewneck',
    'hafen-hoodie':     'The Hafen Hoodie'
  };

  // ── Open native PDP or build rich product view ──
  function actionGoToProduct(action) {
    var p = PRODUCTS[action.productId];
    if (!p) return;

    var seasonalPage = p.page;
    var currentPath = window.location.pathname;
    var pdpName = PRODUCT_NAME_MAP[action.productId] || p.name;

    console.log('[Rainburg Agent] goToProduct:', action.productId, '→ pdpName:', pdpName,
      '| current:', currentPath, '| target:', seasonalPage);

    // ── Case 1: On the correct seasonal page — use native PDP overlay ──
    if (currentPath === seasonalPage && typeof window.openPDP === 'function') {
      // Find color index for the requested color
      var colorIndex = 0;
      if (action.color && typeof window.PRODUCTS !== 'undefined' && window.PRODUCTS[pdpName]) {
        var reqColor = action.color.toLowerCase();
        var pageColors = window.PRODUCTS[pdpName].colors;
        for (var ci = 0; ci < pageColors.length; ci++) {
          if (pageColors[ci].name.toLowerCase().indexOf(reqColor) !== -1) {
            colorIndex = ci;
            break;
          }
        }
      }
      console.log('[Rainburg Agent] On correct page — calling native openPDP("' + pdpName + '", ' + colorIndex + ')');
      closeOverlay();
      // Raise PDP z-index above ElevenLabs widget so PDP is fully clickable
      elevateNativeOverlays();
      window.openPDP(pdpName, colorIndex);
      // Watch for native PDP close to restore z-index
      var pdpOverlayEl = document.getElementById('pdpOverlay');
      if (pdpOverlayEl) {
        var pdpObserver = new MutationObserver(function (mutations) {
          for (var m = 0; m < mutations.length; m++) {
            if (!pdpOverlayEl.classList.contains('open')) {
              console.log('[Rainburg Agent] Native PDP closed — restoring z-index');
              restoreNativeZIndex();
              pdpObserver.disconnect();
              break;
            }
          }
        });
        pdpObserver.observe(pdpOverlayEl, { attributes: true, attributeFilter: ['class'] });
      }
      showStatus(p.name + ' — full product page', '');
      setTimeout(hideStatus, 4000);
      return;
    }

    // ── Case 2: Different page — build rich PDP in the agent overlay ──
    console.log('[Rainburg Agent] Different page — building rich PDP in overlay');
    var body = document.getElementById('rb-results-body');
    var header = overlay.querySelector('h3');
    header.textContent = p.name;
    overlay.classList.add('rb-wide');

    var activeColor = (action.color || p.colors[0]).toLowerCase();
    var imgUrl = getProductImageForColor(action.productId, activeColor);

    // Build color swatches
    var swatchesHTML = '';
    for (var s = 0; s < p.colors.length; s++) {
      var c = p.colors[s].toLowerCase();
      var hex = COLOR_HEX[c] || '#888';
      var isActive = c === activeColor;
      swatchesHTML += '<div class="rb-go-swatch" data-color="' + c + '" ' +
        'style="width:36px;height:36px;border-radius:50%;background:' + hex +
        ';border:' + (isActive ? '3px solid #b55a30' : '2px solid rgba(139,155,168,0.3)') +
        ';cursor:pointer;transition:all 0.2s;" title="' + p.colors[s] + '"></div>';
    }

    // Build size buttons
    var sizes = ['XS', 'S', 'M', 'L', 'XL'];
    var sizeBtnsHTML = '';
    for (var z = 0; z < sizes.length; z++) {
      var isDefault = sizes[z] === 'M' ? ' rb-size-active' : '';
      sizeBtnsHTML += '<button class="rb-size-btn' + isDefault + '" data-size="' + sizes[z] + '">' + sizes[z] + '</button>';
    }

    body.innerHTML =
      '<div class="rb-pdp-full" style="background:#f0ece6;color:#1a1f25;overflow-y:auto;max-height:calc(100vh - 72px);max-height:calc(100dvh - 72px);-webkit-overflow-scrolling:touch;">' +
        '<div class="rb-pdp-grid" style="display:grid;grid-template-columns:1fr 1fr;gap:0;min-height:100vh;">' +
          '<div class="rb-pdp-gallery-col" style="padding:32px;display:flex;flex-direction:column;align-items:center;">' +
            '<img id="rb-go-img" src="' + imgUrl + '" alt="' + p.name + '" ' +
              'style="width:100%;max-width:500px;aspect-ratio:4/5;object-fit:cover;border-radius:6px;margin-bottom:16px;">' +
            '<div style="display:flex;gap:10px;flex-wrap:wrap;justify-content:center;">' + swatchesHTML + '</div>' +
          '</div>' +
          '<div class="rb-pdp-info-col" style="padding:40px 32px 40px 0;">' +
            '<div style="font-size:11px;letter-spacing:0.12em;text-transform:uppercase;color:#8b9ba8;margin-bottom:12px;">' +
              p.season.charAt(0).toUpperCase() + p.season.slice(1) + ' 2026 Collection</div>' +
            '<h1 style="font-family:Georgia,serif;font-size:32px;font-weight:400;margin:0 0 8px;color:#1a1f25;">' + p.name + '</h1>' +
            '<div id="rb-go-color-name" style="font-size:13px;color:#8b9ba8;margin-bottom:16px;">' +
              (action.color || p.colors[0]) + '</div>' +
            '<div style="font-size:22px;font-weight:500;margin-bottom:24px;color:#1a1f25;">&euro;' + p.price + '</div>' +
            '<p style="font-size:14px;line-height:1.7;color:#5a6040;margin-bottom:32px;">' +
              'A premium ' + p.category + ' from the Rainburg ' + p.season + ' 2026 collection. ' +
              'Designed in Hamburg with European-sourced materials.</p>' +
            '<div style="margin-bottom:32px;">' +
              '<div style="font-size:11px;letter-spacing:0.1em;text-transform:uppercase;color:#8b9ba8;margin-bottom:12px;">Specifications</div>' +
              '<div style="font-size:13px;color:#5a6040;padding:10px 0;border-bottom:1px solid #d8d0c8;">Designed in Hamburg</div>' +
              '<div style="font-size:13px;color:#5a6040;padding:10px 0;border-bottom:1px solid #d8d0c8;">European materials</div>' +
              '<div style="font-size:13px;color:#5a6040;padding:10px 0;border-bottom:1px solid #d8d0c8;">Lifetime repair guarantee</div>' +
            '</div>' +
            '<div style="margin-bottom:16px;">' +
              '<div style="font-size:11px;letter-spacing:0.1em;text-transform:uppercase;color:#8b9ba8;margin-bottom:10px;">Size</div>' +
              '<div style="display:flex;gap:8px;">' + sizeBtnsHTML + '</div>' +
            '</div>' +
            '<button id="rb-go-add-cart" style="width:100%;padding:16px;background:#1a1f25;color:#fff;border:none;' +
              'font-size:13px;font-weight:500;letter-spacing:0.1em;text-transform:uppercase;cursor:pointer;' +
              'transition:background 0.2s;">Add to Bag</button>' +
            '<div style="margin-top:16px;text-align:center;">' +
              '<a href="' + seasonalPage + '?agent=1" style="font-size:12px;color:#8b9ba8;text-decoration:underline;">' +
              'View in ' + p.season.charAt(0).toUpperCase() + p.season.slice(1) + ' collection &rarr;</a>' +
            '</div>' +
          '</div>' +
        '</div>' +
      '</div>';

    // Inject size button styles
    var sizeStyle = document.createElement('style');
    sizeStyle.textContent =
      '.rb-size-btn{padding:10px 18px;border:1px solid #d8d0c8;background:transparent;color:#1a1f25;' +
      'font-size:13px;cursor:pointer;transition:all 0.2s;}' +
      '.rb-size-btn:hover{border-color:#8b9ba8;}' +
      '.rb-size-active{background:#1a1f25 !important;color:#fff !important;border-color:#1a1f25 !important;}';
    body.appendChild(sizeStyle);

    // Attach color swatch listeners
    var swatches = body.querySelectorAll('.rb-go-swatch');
    var goImg = document.getElementById('rb-go-img');
    var goColorName = document.getElementById('rb-go-color-name');
    for (var sw = 0; sw < swatches.length; sw++) {
      (function (dot) {
        dot.addEventListener('click', function (e) {
          e.stopPropagation();
          var cl = dot.getAttribute('data-color');
          goImg.src = getProductImageForColor(action.productId, cl);
          goColorName.textContent = dot.getAttribute('title');
          for (var d = 0; d < swatches.length; d++) {
            swatches[d].style.border = '2px solid rgba(139,155,168,0.3)';
          }
          dot.style.border = '3px solid #b55a30';
        });
      })(swatches[sw]);
    }

    // Attach size button listeners
    var sizeBtns = body.querySelectorAll('.rb-size-btn');
    for (var sb = 0; sb < sizeBtns.length; sb++) {
      (function (btn) {
        btn.addEventListener('click', function () {
          for (var b = 0; b < sizeBtns.length; b++) sizeBtns[b].classList.remove('rb-size-active');
          btn.classList.add('rb-size-active');
        });
      })(sizeBtns[sb]);
    }

    // Attach Add to Bag button listener
    var addCartBtn = document.getElementById('rb-go-add-cart');
    if (addCartBtn) {
      addCartBtn.addEventListener('click', function () {
        var selectedSize = 'M';
        var activeSize = body.querySelector('.rb-size-active');
        if (activeSize) selectedSize = activeSize.getAttribute('data-size');
        var selectedColor = activeColor;
        var activeColorDot = body.querySelector('.rb-go-swatch[style*="3px solid #b55a30"]');
        if (activeColorDot) selectedColor = activeColorDot.getAttribute('data-color');
        actionCartAdd({ productId: action.productId, color: selectedColor, size: selectedSize });
      });
    }

    openOverlay();
    showStatus(p.name + ' — full product page', '');
    setTimeout(hideStatus, 4000);
  }

  // ── Scroll to a section ──
  function actionScrollTo(action) {
    var section = action.section;
    var el = document.getElementById(section) ||
             document.querySelector('[data-section="' + section + '"]') ||
             document.querySelector('.' + section);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  // ── Cart: Add item using the page's native cart system ──
  function actionCartAdd(action) {
    var productId = action.productId;
    var p = productId ? PRODUCTS[productId] : null;
    if (!p) {
      showStatus('Cannot add — product not found', '');
      setTimeout(hideStatus, 3000);
      console.warn('[Rainburg Agent] cartAdd: no product found for', productId);
      return;
    }

    var color = action.color || p.colors[0];
    var size = action.size || 'M';
    var imgUrl = getProductImageForColor(productId, color.toLowerCase());

    console.log('[Rainburg Agent] Adding to cart:', p.name, color, size);

    // Initialize window.cart if it doesn't exist (e.g. on homepage or test page)
    if (typeof window.cart === 'undefined') {
      console.log('[Rainburg Agent] window.cart not found — initializing');
      window.cart = [];
    }

    var cartItem = {
      name: p.name,
      color: color,
      size: size.toUpperCase(),
      eurPrice: p.price,
      img: imgUrl,
      id: p.name + '-' + color + '-' + size
    };

    var prevLength = window.cart.length;
    window.cart.push(cartItem);

    // ── Validation: confirm item was actually added ──
    if (window.cart.length === prevLength + 1) {
      console.log('[Rainburg Agent] ✅ Cart add VERIFIED. Cart now has', window.cart.length, 'items:', JSON.stringify(cartItem));
      showAgentToast(p.name + ' (' + color + ', ' + size.toUpperCase() + ') added to bag', '🛍️');
    } else {
      console.error('[Rainburg Agent] ❌ Cart add FAILED. Length before:', prevLength, 'after:', window.cart.length);
      showAgentToast('Failed to add ' + p.name + ' — please try again', '⚠️');
    }

    // Persist cart to sessionStorage
    saveCart();

    // Update server-side cart count
    if (ws && ws.readyState === 1) {
      var _cs = getCartSummary(); ws.send(JSON.stringify({ type: 'cart_updated_count', count: _cs.count, items: _cs.items, total: _cs.total }));
    }

    // Render via page's native renderCart if available
    if (typeof window.renderCart === 'function') {
      window.renderCart();
    }
  }

  // ── Cart: View — open the page's native cart panel ──
  function actionCartView() {
    // Check if cart is already open — if so, just confirm, don't re-trigger
    var cartOverlayEl = document.getElementById('cartOverlay');
    if (cartOverlayEl && cartOverlayEl.classList.contains('open')) {
      console.log('[Rainburg Agent] Cart panel is already open — skipping openCart');
      showStatus('Your bag is open', '');
      setTimeout(hideStatus, 2000);
      return;
    }

    if (typeof window.openCart === 'function') {
      console.log('[Rainburg Agent] Opening native cart panel');
      closeOverlay();
      // Raise cart z-index above ElevenLabs widget so cart is fully clickable
      elevateNativeOverlays();
      window.openCart();
      cartPanelOpen = true;
      // Watch for cart close to restore z-index
      if (cartOverlayEl) {
        var cartObserver = new MutationObserver(function (mutations) {
          if (!cartOverlayEl.classList.contains('open')) {
            cartPanelOpen = false;
            restoreNativeZIndex();
            cartObserver.disconnect();
          }
        });
        cartObserver.observe(cartOverlayEl, { attributes: true, attributeFilter: ['class'] });
      }
    } else {
      showStatus('Cart not available on this page', '');
      setTimeout(hideStatus, 3000);
    }
  }

  // ── Cart: Close — close the native cart panel ──
  function actionCartClose() {
    if (typeof window.closeCart === 'function') {
      console.log('[Rainburg Agent] Closing native cart panel');
      window.closeCart();
      cartPanelOpen = false;
      restoreNativeZIndex();
    } else {
      // Try closing via DOM class toggle (fallback)
      var cartOverlayEl = document.getElementById('cartOverlay');
      if (cartOverlayEl && cartOverlayEl.classList.contains('open')) {
        cartOverlayEl.classList.remove('open');
        var cartPanelEl = document.getElementById('cartPanel');
        if (cartPanelEl) cartPanelEl.classList.remove('open');
        cartPanelOpen = false;
        console.log('[Rainburg Agent] Cart panel closed via classList fallback');
        restoreNativeZIndex();
        document.body.style.overflow = '';
      }
    }
  }

  // ── Cart: Remove item by voice ──
  function actionCartRemove(action) {
    if (typeof window.cart === 'undefined' || window.cart.length === 0) {
      showAgentToast('Your bag is empty', '\uD83D\uDC5C');
      return;
    }

    // ── "Remove all" / "clear cart" ──
    if (action.removeAll) {
      var count = window.cart.length;
      // IMPORTANT: Clear in-place (don't reassign) so page's local `cart` var stays in sync
      window.cart.splice(0, window.cart.length);
      saveCart();
      console.log('[Rainburg Agent] ✅ Cleared entire cart (' + count + ' items)');
      showAgentToast('All ' + count + ' items removed from your bag', '\uD83D\uDDD1\uFE0F');
      if (ws && ws.readyState === 1) {
        ws.send(JSON.stringify({ type: 'cart_updated_count', count: 0, items: [], total: 0 }));
      }
      // Re-render the cart panel and update all badge counts
      if (typeof window.renderCart === 'function') window.renderCart();
      var badges = document.querySelectorAll('.bag-count, .cart-count, .nav-bag-count, [data-cart-count], #bagCount, #cartCount');
      for (var b = 0; b < badges.length; b++) badges[b].textContent = '0';
      return;
    }

    var productId = action.productId;
    var color = (action.color || '').toLowerCase();
    var removedItem = null;
    var removeIndex = -1;

    // Find the item to remove — match by productId and optionally color
    for (var i = 0; i < window.cart.length; i++) {
      var item = window.cart[i];
      var itemName = (item.name || '').toLowerCase();
      var itemColor = (item.color || '').toLowerCase();
      var p = productId ? PRODUCTS[productId] : null;
      var pName = p ? p.name.toLowerCase() : '';

      // Match by product name (from catalog lookup) or direct name match
      var nameMatch = (pName && itemName === pName) || itemName.indexOf(productId) !== -1;
      var colorMatch = !color || itemColor.indexOf(color) !== -1;

      if (nameMatch && colorMatch) {
        removeIndex = i;
        removedItem = item;
        break;
      }
    }

    if (removeIndex === -1) {
      // If no specific match, try removing by keyword in item name
      var keyword = (action.keyword || action.productId || '').toLowerCase();
      for (var j = 0; j < window.cart.length; j++) {
        if (window.cart[j].name.toLowerCase().indexOf(keyword) !== -1) {
          removeIndex = j;
          removedItem = window.cart[j];
          break;
        }
      }
    }

    if (removeIndex >= 0 && removedItem) {
      window.cart.splice(removeIndex, 1);
      saveCart();
      console.log('[Rainburg Agent] ✅ Removed from cart:', removedItem.name, removedItem.color);
      showAgentToast(removedItem.name + ' (' + removedItem.color + ') removed', '\uD83D\uDDD1\uFE0F');

      // Update server-side cart count
      if (ws && ws.readyState === 1) {
        var _cs = getCartSummary(); ws.send(JSON.stringify({ type: 'cart_updated_count', count: _cs.count, items: _cs.items, total: _cs.total }));
      }

      // Re-render cart if visible and update badge counts
      if (typeof window.renderCart === 'function') {
        window.renderCart();
      }
      var badges = document.querySelectorAll('.bag-count, .cart-count, .nav-bag-count, [data-cart-count], #bagCount, #cartCount');
      for (var b = 0; b < badges.length; b++) badges[b].textContent = String(window.cart.length);
    } else {
      console.warn('[Rainburg Agent] cartRemove: no matching item for', productId, color);
      showAgentToast('Could not find that item in your bag', '\u26A0\uFE0F');
    }
  }

  // ── Navigate Page: Load ancillary pages (about, size guide, reviews, shipping) in overlay ──
  function actionNavigatePage(action) {
    var pageUrl = action.url;
    if (!pageUrl) return;

    console.log('[Rainburg Agent] Loading ancillary page in overlay:', pageUrl);
    showStatus('Loading page...', '');

    var header = overlay.querySelector('h3');
    header.textContent = action.title || 'Page';
    overlay.classList.add('rb-wide');

    var body = document.getElementById('rb-results-body');
    body.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:200px;color:#8b9ba8;">' +
      '<div style="text-align:center;">' +
      '<div style="font-size:24px;margin-bottom:8px;">Loading...</div>' +
      '<div style="font-size:12px;opacity:0.6;">Keeping your voice session active</div>' +
      '</div></div>';
    openOverlay();

    fetch(pageUrl, { credentials: 'same-origin' })
      .then(function (resp) {
        if (!resp.ok) throw new Error('HTTP ' + resp.status);
        return resp.text();
      })
      .then(function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');

        // Extract the main content — try common content selectors
        var content = doc.querySelector('.page-content') ||
                      doc.querySelector('main') ||
                      doc.querySelector('.shopify-section') ||
                      doc.querySelector('[role="main"]') ||
                      doc.body;

        if (content) {
          // Clean up: remove scripts, navigation, footers
          var removeSelectors = ['script', 'nav', 'footer', 'header', '.site-header', '.site-footer',
            'elevenlabs-convai', '#rb-agent-btn', '#rb-agent-status', '#rb-results-overlay'];
          removeSelectors.forEach(function (sel) {
            var els = content.querySelectorAll(sel);
            for (var i = 0; i < els.length; i++) els[i].remove();
          });

          // Grab ALL <style> tags from the full document (not just content area)
          // These contain CSS variables, background-images, and layout rules
          var pageStyles = '';
          var styleTags = doc.querySelectorAll('style');
          for (var sti = 0; sti < styleTags.length; sti++) {
            pageStyles += styleTags[sti].innerHTML + '\n';
          }

          // Fix relative image/video URLs → absolute (Shopify CDN)
          var origin = window.location.origin;
          var mediaEls = content.querySelectorAll('img, video, source');
          for (var mi = 0; mi < mediaEls.length; mi++) {
            var el = mediaEls[mi];
            ['src', 'srcset', 'poster'].forEach(function (attr) {
              var val = el.getAttribute(attr);
              if (val && val.indexOf('//') === -1 && val.charAt(0) === '/') {
                el.setAttribute(attr, origin + val);
              }
            });
          }
          // Fix background-image inline styles
          var allEls = content.querySelectorAll('[style]');
          for (var si = 0; si < allEls.length; si++) {
            var style = allEls[si].getAttribute('style');
            if (style && style.indexOf('url(') !== -1) {
              allEls[si].setAttribute('style', style.replace(/url\(\s*['"]?\//g, 'url(' + origin + '/'));
            }
          }
          // Also fix URLs inside the grabbed page styles
          pageStyles = pageStyles.replace(/url\(\s*['"]?\//g, 'url(' + origin + '/');

          body.innerHTML =
            '<div class="rb-page-content" style="overflow-y:auto;max-height:calc(100vh - 72px);">' +
              '<style>' +
                '/* Include ALL styles from the original page */' +
                pageStyles +
                '/* Override layout-specific styles for overlay context */' +
                '#rb-results-body main { margin-top: 0 !important; padding: 32px !important; }' +
                '#rb-results-body nav { display: none !important; }' +
                '#rb-results-body footer { display: none !important; }' +
                '#rb-results-body img { max-width: 100%; height: auto; }' +
                '#rb-results-body video { max-width: 100%; height: auto; }' +
                '/* Ensure body-level styles scope to our container */' +
                '.rb-page-content { font-family: "Inter", -apple-system, sans-serif; line-height: 1.6; }' +
              '</style>' +
              content.innerHTML +
            '</div>';
          showStatus(action.title || 'Page loaded', '');
        } else {
          body.innerHTML = '<div style="padding:32px;color:#8b9ba8;">Could not load page content. ' +
            '<a href="' + pageUrl + '?agent=1" style="color:#b55a30;">Open in new tab</a></div>';
          showStatus('Page load issue', '');
        }
        setTimeout(hideStatus, 3000);
      })
      .catch(function (err) {
        console.error('[Rainburg Agent] Failed to fetch page:', err);
        body.innerHTML = '<div style="padding:32px;color:#8b9ba8;">Could not load this page. ' +
          '<a href="' + pageUrl + '?agent=1" style="color:#b55a30;">Open in new tab</a></div>';
        showStatus('Page could not be loaded', '');
        setTimeout(hideStatus, 3000);
      });
  }

  // ── Checkout: Voice-guided checkout flow in overlay ──
  function actionCheckout(action) {
    var cartItems = (typeof window.cart !== 'undefined') ? window.cart : [];
    console.log('[Rainburg Agent] Checkout with', cartItems.length, 'items');

    if (cartItems.length === 0) {
      showStatus('Your bag is empty', '');
      setTimeout(hideStatus, 3000);
      return;
    }

    var body = document.getElementById('rb-results-body');
    var header = overlay.querySelector('h3');
    header.textContent = 'Checkout';
    overlay.classList.add('rb-wide');

    // Calculate total
    var total = 0;
    var itemsHTML = '';
    for (var i = 0; i < cartItems.length; i++) {
      var item = cartItems[i];
      total += item.eurPrice;
      itemsHTML +=
        '<div style="display:flex;gap:16px;padding:16px 0;border-bottom:1px solid #d8d0c8;">' +
          '<img src="' + item.img + '" alt="' + item.name + '" style="width:80px;height:100px;object-fit:cover;border-radius:4px;">' +
          '<div style="flex:1;">' +
            '<div style="font-weight:500;margin-bottom:4px;">' + item.name + '</div>' +
            '<div style="font-size:13px;color:#8b9ba8;margin-bottom:2px;">' + item.color + ' / Size ' + item.size + '</div>' +
            '<div style="font-weight:500;">&euro;' + item.eurPrice + '</div>' +
          '</div>' +
        '</div>';
    }

    body.innerHTML =
      '<div style="background:#f0ece6;color:#1a1f25;overflow-y:auto;max-height:calc(100vh - 72px);max-height:calc(100dvh - 72px);padding:0;-webkit-overflow-scrolling:touch;">' +
        '<div style="max-width:800px;margin:0 auto;padding:32px 32px 120px;">' +
          // Step indicator
          '<div style="display:flex;gap:24px;margin-bottom:32px;font-size:12px;letter-spacing:0.1em;text-transform:uppercase;color:#8b9ba8;">' +
            '<span id="rb-step-1" style="color:#b55a30;font-weight:600;">1. Review</span>' +
            '<span id="rb-step-2">2. Shipping</span>' +
            '<span id="rb-step-3">3. Payment</span>' +
          '</div>' +

          // Step 1: Order Review
          '<div id="rb-checkout-step1">' +
            '<h2 style="font-family:Georgia,serif;font-size:24px;font-weight:400;margin:0 0 24px;">Review your order</h2>' +
            '<div style="margin-bottom:24px;">' + itemsHTML + '</div>' +
            '<div style="display:flex;justify-content:space-between;padding:16px 0;border-top:2px solid #1a1f25;font-size:18px;font-weight:500;">' +
              '<span>Total</span><span>&euro;' + total + '</span>' +
            '</div>' +
            '<p style="font-size:12px;color:#8b9ba8;margin:8px 0 24px;">Free shipping on orders over &euro;100 &middot; 30-day returns</p>' +
            '<button id="rb-checkout-next1" style="width:100%;padding:16px;background:#1a1f25;color:#fff;border:none;' +
              'font-size:13px;font-weight:500;letter-spacing:0.1em;text-transform:uppercase;cursor:pointer;">Continue to Shipping</button>' +
            '<p style="text-align:center;margin-top:12px;font-size:12px;color:#8b9ba8;">' +
              'Or say "continue" to proceed</p>' +
          '</div>' +

          // Step 2: Shipping
          '<div id="rb-checkout-step2" style="display:none;">' +
            '<h2 style="font-family:Georgia,serif;font-size:24px;font-weight:400;margin:0 0 24px;">Shipping details</h2>' +
            '<div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;">' +
              '<div><label style="font-size:11px;letter-spacing:0.08em;text-transform:uppercase;color:#8b9ba8;display:block;margin-bottom:6px;">First Name</label>' +
                '<input id="rb-ship-fname" type="text" style="width:100%;padding:12px;border:1px solid #d8d0c8;font-size:14px;background:#fff;box-sizing:border-box;"></div>' +
              '<div><label style="font-size:11px;letter-spacing:0.08em;text-transform:uppercase;color:#8b9ba8;display:block;margin-bottom:6px;">Last Name</label>' +
                '<input id="rb-ship-lname" type="text" style="width:100%;padding:12px;border:1px solid #d8d0c8;font-size:14px;background:#fff;box-sizing:border-box;"></div>' +
            '</div>' +
            '<div style="margin-top:16px;"><label style="font-size:11px;letter-spacing:0.08em;text-transform:uppercase;color:#8b9ba8;display:block;margin-bottom:6px;">Email</label>' +
              '<input id="rb-ship-email" type="email" style="width:100%;padding:12px;border:1px solid #d8d0c8;font-size:14px;background:#fff;box-sizing:border-box;"></div>' +
            '<div style="margin-top:16px;"><label style="font-size:11px;letter-spacing:0.08em;text-transform:uppercase;color:#8b9ba8;display:block;margin-bottom:6px;">Address</label>' +
              '<input id="rb-ship-addr" type="text" style="width:100%;padding:12px;border:1px solid #d8d0c8;font-size:14px;background:#fff;box-sizing:border-box;"></div>' +
            '<div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-top:16px;">' +
              '<div><label style="font-size:11px;letter-spacing:0.08em;text-transform:uppercase;color:#8b9ba8;display:block;margin-bottom:6px;">City</label>' +
                '<input id="rb-ship-city" type="text" style="width:100%;padding:12px;border:1px solid #d8d0c8;font-size:14px;background:#fff;box-sizing:border-box;"></div>' +
              '<div><label style="font-size:11px;letter-spacing:0.08em;text-transform:uppercase;color:#8b9ba8;display:block;margin-bottom:6px;">Postal Code</label>' +
                '<input id="rb-ship-zip" type="text" style="width:100%;padding:12px;border:1px solid #d8d0c8;font-size:14px;background:#fff;box-sizing:border-box;"></div>' +
            '</div>' +
            '<div style="margin-top:16px;"><label style="font-size:11px;letter-spacing:0.08em;text-transform:uppercase;color:#8b9ba8;display:block;margin-bottom:6px;">Country</label>' +
              '<select id="rb-ship-country" style="width:100%;padding:12px;border:1px solid #d8d0c8;font-size:14px;background:#fff;box-sizing:border-box;">' +
                '<option value="DE">Germany</option><option value="AT">Austria</option><option value="CH">Switzerland</option>' +
                '<option value="DK">Denmark</option><option value="SE">Sweden</option><option value="FR">France</option>' +
                '<option value="NL">Netherlands</option><option value="BE">Belgium</option><option value="GB">United Kingdom</option>' +
                '<option value="IE">Ireland</option><option value="IT">Italy</option><option value="ES">Spain</option>' +
              '</select></div>' +
            '<button id="rb-checkout-next2" style="width:100%;padding:16px;background:#1a1f25;color:#fff;border:none;margin-top:24px;' +
              'font-size:13px;font-weight:500;letter-spacing:0.1em;text-transform:uppercase;cursor:pointer;">Continue to Payment</button>' +
            '<p style="text-align:center;margin-top:12px;font-size:12px;color:#8b9ba8;">' +
              'Tell me your details or type them in</p>' +
          '</div>' +

          // Step 3: Payment / Confirm
          '<div id="rb-checkout-step3" style="display:none;">' +
            '<h2 style="font-family:Georgia,serif;font-size:24px;font-weight:400;margin:0 0 24px;">Complete your order</h2>' +
            '<div id="rb-order-summary" style="background:#fff;padding:20px;border-radius:6px;margin-bottom:24px;border:1px solid #d8d0c8;"></div>' +
            '<div style="background:#fff;padding:20px;border-radius:6px;border:1px solid #d8d0c8;margin-bottom:24px;">' +
              '<div style="font-size:11px;letter-spacing:0.1em;text-transform:uppercase;color:#8b9ba8;margin-bottom:12px;">Payment</div>' +
              '<p style="font-size:14px;color:#5a6040;margin:0;">You\'ll be redirected to Shopify\'s secure payment page to complete your purchase.</p>' +
            '</div>' +
            '<button id="rb-checkout-pay" style="width:100%;padding:16px;background:#b55a30;color:#fff;border:none;' +
              'font-size:14px;font-weight:500;letter-spacing:0.1em;text-transform:uppercase;cursor:pointer;border-radius:4px;">Proceed to Payment &rarr;</button>' +
            '<p style="text-align:center;margin-top:12px;font-size:12px;color:#8b9ba8;">' +
              'Say "place my order" or click the button</p>' +
          '</div>' +
        '</div>' +
      '</div>';

    // Step navigation
    var step1 = document.getElementById('rb-checkout-step1');
    var step2 = document.getElementById('rb-checkout-step2');
    var step3 = document.getElementById('rb-checkout-step3');
    var stepLabels = [document.getElementById('rb-step-1'), document.getElementById('rb-step-2'), document.getElementById('rb-step-3')];

    function goToStep(n) {
      step1.style.display = n === 1 ? '' : 'none';
      step2.style.display = n === 2 ? '' : 'none';
      step3.style.display = n === 3 ? '' : 'none';
      for (var sl = 0; sl < stepLabels.length; sl++) {
        stepLabels[sl].style.color = sl < n ? '#b55a30' : '#8b9ba8';
        stepLabels[sl].style.fontWeight = sl < n ? '600' : '400';
      }
      if (n === 3) buildOrderSummary();
    }

    function buildOrderSummary() {
      var sumEl = document.getElementById('rb-order-summary');
      var fname = document.getElementById('rb-ship-fname').value || '';
      var lname = document.getElementById('rb-ship-lname').value || '';
      var email = document.getElementById('rb-ship-email').value || '';
      var addr = document.getElementById('rb-ship-addr').value || '';
      var city = document.getElementById('rb-ship-city').value || '';
      var zip = document.getElementById('rb-ship-zip').value || '';
      var country = document.getElementById('rb-ship-country');
      var countryName = country.options[country.selectedIndex].text;

      var itemLines = '';
      for (var j = 0; j < cartItems.length; j++) {
        itemLines += '<div style="display:flex;justify-content:space-between;padding:6px 0;font-size:13px;">' +
          '<span>' + cartItems[j].name + ' (' + cartItems[j].color + ', ' + cartItems[j].size + ')</span>' +
          '<span>&euro;' + cartItems[j].eurPrice + '</span></div>';
      }

      sumEl.innerHTML =
        '<div style="font-size:11px;letter-spacing:0.1em;text-transform:uppercase;color:#8b9ba8;margin-bottom:12px;">Order Summary</div>' +
        itemLines +
        '<div style="display:flex;justify-content:space-between;padding:10px 0;margin-top:8px;border-top:1px solid #d8d0c8;font-weight:500;">' +
          '<span>Total</span><span>&euro;' + total + '</span></div>' +
        '<div style="margin-top:16px;font-size:11px;letter-spacing:0.1em;text-transform:uppercase;color:#8b9ba8;margin-bottom:8px;">Shipping to</div>' +
        '<div style="font-size:14px;">' +
          (fname || lname ? fname + ' ' + lname + '<br>' : '') +
          (addr ? addr + '<br>' : '') +
          (zip || city ? zip + ' ' + city + '<br>' : '') +
          countryName +
          (email ? '<br>' + email : '') +
        '</div>';
    }

    document.getElementById('rb-checkout-next1').addEventListener('click', function () { goToStep(2); });
    document.getElementById('rb-checkout-next2').addEventListener('click', function () { goToStep(3); });
    document.getElementById('rb-checkout-pay').addEventListener('click', function () {
      showStatus('Redirecting to payment...', '');
      setTimeout(function () { window.location.href = '/checkout'; }, 1000);
    });

    // Store goToStep on window so voice agent can trigger steps
    window.rbCheckoutStep = goToStep;

    openOverlay();
    showStatus('Review your order', '');
    setTimeout(hideStatus, 4000);
  }

  // ─── RESULTS OVERLAY RENDERING ─────────────────────────────────────────────

  var COLOR_HEX = {
    'sage green':'#7d9b76','sage':'#7d9b76','coral':'#e87461','sky blue':'#87ceeb','sand':'#c2b280','black':'#1a1a1a',
    'moss':'#4a6741','charcoal':'#36454f','navy':'#1a2a4a','off-white':'#f5f0e8','clay':'#b66a50',
    'olive':'#556b2f','khaki':'#bdb76b','stone':'#8a8070','amber':'#cf8a30','walnut':'#5c4033',
    'cream':'#f5f0e0','dark brown':'#3b2a1a','midnight':'#191970','green':'#2e5a2e','forest':'#355e3b',
    'white':'#f5f5f5','burgundy':'#722f37','steel blue':'#4682b4','slate':'#708090',
    // Kids collection colors
    'pink':'#e8a0b0','rose':'#c4a0a0','lavender':'#9b8ec4','sunshine yellow':'#f0c75e',
    'forest green':'#4a8c6f','ocean blue':'#2a7ab5','ocean':'#7ec8e3','dusty rose':'#c4a08a',
    'mint':'#7ac4a8','tan':'#c8b898','rust':'#a0522d','orange':'#e8884f','yellow':'#f0c75e',
    'blue':'#7ec8e3','brown':'#6b4e3d',
  };

  function renderResults(results, filters) {
    var body = document.getElementById('rb-results-body');
    var header = overlay.querySelector('h3');

    if (results.length === 0) {
      header.textContent = 'No matches found';
      body.innerHTML = '<p style="color:#8b9ba8;font-size:13px;padding:16px;">No products match your request. Try a different color, season, or category.</p>';
    } else {
      var parts = [];
      if (filters.color) parts.push(filters.color);
      if (filters.season) parts.push(filters.season);
      if (filters.category) parts.push(filters.category + (results.length !== 1 ? 's' : ''));
      header.textContent = results.length + ' result' + (results.length !== 1 ? 's' : '') + (parts.length ? ' \u2014 ' + parts.join(' ') : '');

      body.innerHTML = '';

      results.forEach(function (r, i) {
        var p = r.product;

        // Navigate to seasonal page with #products anchor (no individual product pages)
        var linkUrl = p.page + '#products';

        // Get image — use matched color if available, else first color
        var imgUrl = r.matchedColor
          ? getProductImageForColor(r.id, r.matchedColor)
          : getProductImageUrl(r.id);

        // Build color dots
        var dotsHtml = p.colors.map(function (c) {
          var hex = COLOR_HEX[c.toLowerCase()] || '#888';
          var highlighted = r.matchedColor && c.toLowerCase().indexOf(r.matchedColor.toLowerCase()) !== -1;
          var border = highlighted ? '2px solid #b55a30' : '1px solid rgba(139,155,168,0.3)';
          return '<div class="rb-color-dot" style="background:' + hex + ';border:' + border + ';" title="' + c + '"></div>';
        }).join('');

        // Card uses data-attributes for event delegation (no inline JS — CSP safe)
        var cardHtml =
          '<div class="rb-product-card" data-product-id="' + r.id + '"' +
            ' data-link="' + linkUrl + '"' +
            ' data-img="' + imgUrl + '"' +
            ' data-name="' + p.name + '"' +
            ' data-price="' + p.price + '">' +
            '<div class="rb-product-card-inner">' +
              '<div class="rb-product-img-wrap">' +
                '<img src="' + imgUrl + '" alt="' + p.name + '" loading="lazy">' +
              '</div>' +
              '<div class="rb-product-info">' +
                '<p class="rb-product-name">' + p.name + '</p>' +
                '<p class="rb-product-meta">' + p.season.charAt(0).toUpperCase() + p.season.slice(1) + ' 2026 &middot; <span class="rb-product-price">&euro;' + p.price + '</span></p>' +
                '<div class="rb-product-colors">' + dotsHtml + '</div>' +
              '</div>' +
            '</div>' +
          '</div>';

        body.insertAdjacentHTML('beforeend', cardHtml);
      });
    }

    // Attach click + hover listeners to newly rendered cards
    attachCardListeners();

    openOverlay();

    // Track displayed products for conversation context
    if (ws && ws.readyState === 1) {
      ws.send(JSON.stringify({
        type: 'products_displayed',
        products: results.map(function (r, i) {
          return { index: i + 1, id: r.id, name: r.product.name, price: r.product.price, season: r.product.season };
        })
      }));
    }
  }

  // ─── Z-INDEX ELEVATION ─────────────────────────────────────────────────────
  // Instead of suppressing pointer-events on the ElevenLabs widget (which causes
  // page lockouts), we RAISE the z-index of native overlays above the widget
  // (z-index 9998). This guarantees the page can never become unclickable.
  var WIDGET_Z = 9998;
  var ELEVATED_Z = WIDGET_Z + 10;        // 10008 — above widget
  var ELEVATED_PANEL_Z = WIDGET_Z + 11;  // 10009 — panel above overlay backdrop

  function elevateNativeOverlays() {
    var cartOverlayEl = document.getElementById('cartOverlay');
    var cartPanelEl = document.getElementById('cartPanel');
    var pdpOverlayEl = document.getElementById('pdpOverlay');
    if (cartOverlayEl) cartOverlayEl.style.zIndex = ELEVATED_Z;
    if (cartPanelEl) cartPanelEl.style.zIndex = ELEVATED_PANEL_Z;
    if (pdpOverlayEl) pdpOverlayEl.style.zIndex = ELEVATED_Z;
  }

  function restoreNativeZIndex() {
    var cartOverlayEl = document.getElementById('cartOverlay');
    var cartPanelEl = document.getElementById('cartPanel');
    var pdpOverlayEl = document.getElementById('pdpOverlay');
    if (cartOverlayEl) cartOverlayEl.style.removeProperty('z-index');
    if (cartPanelEl) cartPanelEl.style.removeProperty('z-index');
    if (pdpOverlayEl) pdpOverlayEl.style.removeProperty('z-index');
  }

  function openOverlay() {
    console.log('[Rainburg Agent] openOverlay() called');
    // Always dismiss hover preview when opening/changing overlay content
    zoomClone.classList.remove('visible');
    // Force-remove then re-add to ensure CSS transition fires even if state is stale
    overlay.classList.remove('open');
    backdrop.classList.remove('visible');
    // Force a reflow so the browser registers the removal before re-adding
    void overlay.offsetHeight;
    overlay.classList.add('open');
    backdrop.classList.add('visible');
    // Agent overlay is at z-99996 — already above everything, no pointer-events needed
    console.log('[Rainburg Agent] Overlay open, classes:', overlay.className, 'backdrop:', backdrop.className);
  }

  function closeOverlay() {
    console.log('[Rainburg Agent] closeOverlay() called');
    overlay.classList.remove('open');
    overlay.classList.remove('rb-wide');
    backdrop.classList.remove('visible');
    zoomClone.classList.remove('visible');
  }

  document.getElementById('rb-results-close').addEventListener('click', closeOverlay);
  backdrop.addEventListener('click', closeOverlay);

  // Prevent clicks inside the overlay from reaching the backdrop
  overlay.addEventListener('click', function (e) {
    e.stopPropagation();
  });

  // ─── DIAGNOSTIC + NUCLEAR UNLOCK SYSTEM ──────────────────────────────────
  // Previous approaches (pointer-events, z-index, safety nets) didn't solve
  // the page lockout. This system takes a fundamentally different approach:
  // 1. DETECT: Use elementsFromPoint() to find what's ACTUALLY blocking clicks
  // 2. DIAGNOSE: Log blocking elements with full detail
  // 3. NUKE: Force-disable any blocking element regardless of what it is

  // Diagnose what elements are intercepting clicks at a given point
  function diagnoseBlockers() {
    var cx = window.innerWidth / 2;
    var cy = window.innerHeight / 2;
    var elements = document.elementsFromPoint(cx, cy);
    var blockers = [];

    for (var i = 0; i < elements.length; i++) {
      var el = elements[i];
      if (el === document.documentElement || el === document.body) continue;
      var cs = window.getComputedStyle(el);
      if (cs.position === 'fixed' || cs.position === 'absolute') {
        var rect = el.getBoundingClientRect();
        // Is it large enough to be a full-screen blocker?
        if (rect.width > window.innerWidth * 0.5 && rect.height > window.innerHeight * 0.5) {
          blockers.push({
            el: el,
            tag: el.tagName.toLowerCase(),
            id: el.id || '(none)',
            classes: el.className || '(none)',
            zIndex: cs.zIndex,
            pointerEvents: cs.pointerEvents,
            opacity: cs.opacity,
            display: cs.display,
            size: Math.round(rect.width) + 'x' + Math.round(rect.height)
          });
        }
      }
    }
    return blockers;
  }

  // Nuclear unlock: force-disable ALL blocking overlays
  function nuclearUnlock() {
    console.log('%c[Rainburg Agent] ☢ NUCLEAR UNLOCK ☢', 'color:red;font-size:14px;font-weight:bold');

    // 1. Close all known agent overlays
    overlay.classList.remove('open', 'rb-wide');
    backdrop.classList.remove('visible');

    // 2. Close all native overlays
    var ids = ['cartOverlay', 'cartPanel', 'pdpOverlay'];
    for (var i = 0; i < ids.length; i++) {
      var el = document.getElementById(ids[i]);
      if (el) {
        el.classList.remove('open');
        el.style.removeProperty('z-index');
        el.style.removeProperty('pointer-events');
      }
    }

    // 3. Restore body
    document.body.style.overflow = '';
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('pointer-events');
    document.body.classList.remove('menu-open');

    // 4. Find and disable ANY remaining full-screen fixed blockers
    var blockers = diagnoseBlockers();
    for (var b = 0; b < blockers.length; b++) {
      var info = blockers[b];
      if (info.pointerEvents !== 'none') {
        console.log('[Rainburg Agent] ☢ Disabling blocker:', info.tag + '#' + info.id,
          'classes="' + info.classes + '"', 'z=' + info.zIndex, info.size,
          'pointer-events=' + info.pointerEvents, 'opacity=' + info.opacity);
        info.el.style.setProperty('pointer-events', 'none', 'important');
      }
    }

    // 5. Find ANY element with position:fixed that covers the viewport
    var allEls = document.querySelectorAll('*');
    for (var j = 0; j < allEls.length; j++) {
      var el2 = allEls[j];
      var cs2 = window.getComputedStyle(el2);
      if (cs2.position === 'fixed' && cs2.pointerEvents !== 'none') {
        var r2 = el2.getBoundingClientRect();
        if (r2.width > window.innerWidth * 0.8 && r2.height > window.innerHeight * 0.8) {
          // Skip the page body/html
          if (el2 === document.body || el2 === document.documentElement) continue;
          // This is a full-screen fixed element with pointer-events — disable it
          console.log('[Rainburg Agent] ☢ Full-screen fixed blocker found:', el2.tagName,
            '#' + (el2.id || ''), 'z=' + cs2.zIndex, 'pe=' + cs2.pointerEvents);
          el2.style.setProperty('pointer-events', 'none', 'important');
        }
      }
    }

    // 6. Also check inside ElevenLabs shadow DOM if accessible
    if (elevenLabsEl && elevenLabsEl.shadowRoot) {
      var shadowFixed = elevenLabsEl.shadowRoot.querySelectorAll('*');
      for (var s = 0; s < shadowFixed.length; s++) {
        var sel = shadowFixed[s];
        var scs = window.getComputedStyle(sel);
        if (scs.position === 'fixed' || scs.position === 'absolute') {
          var sr = sel.getBoundingClientRect();
          if (sr.width > window.innerWidth * 0.5 && sr.height > window.innerHeight * 0.5) {
            console.log('[Rainburg Agent] ☢ Shadow DOM blocker:', sel.tagName,
              sel.className || '', 'z=' + scs.zIndex, 'pe=' + scs.pointerEvents,
              Math.round(sr.width) + 'x' + Math.round(sr.height));
            sel.style.setProperty('pointer-events', 'none', 'important');
          }
        }
      }
    }

    cartPanelOpen = false;
    console.log('[Rainburg Agent] ☢ Nuclear unlock complete');
  }

  // Regular cleanup (runs every 500ms)
  function cleanupUI() {
    var cartOverlayEl = document.getElementById('cartOverlay');
    var pdpOverlayEl = document.getElementById('pdpOverlay');
    var cartOpen = cartOverlayEl && cartOverlayEl.classList.contains('open');
    var pdpOpen = pdpOverlayEl && pdpOverlayEl.classList.contains('open');
    var overlayOpen = overlay.classList.contains('open');

    if (!cartOpen && !pdpOpen && !overlayOpen) {
      // Nothing should be blocking — clean up known elements
      if (backdrop.classList.contains('visible')) {
        console.log('[Rainburg Agent] Safety net: removing stuck backdrop');
        backdrop.classList.remove('visible');
      }
      restoreNativeZIndex();
      document.body.style.removeProperty('overflow');
      document.body.style.overflow = '';
      document.body.classList.remove('menu-open');

      var cartPanelEl = document.getElementById('cartPanel');
      if (cartPanelEl && cartPanelEl.classList.contains('open')) {
        cartPanelEl.classList.remove('open');
      }
      cartPanelOpen = false;

      // ACTIVE DETECTION: check if something is still blocking clicks
      var blockers = diagnoseBlockers();
      var realBlockers = [];
      for (var b = 0; b < blockers.length; b++) {
        if (blockers[b].pointerEvents !== 'none') {
          realBlockers.push(blockers[b]);
        }
      }
      if (realBlockers.length > 0) {
        console.warn('[Rainburg Agent] BLOCKED! Found ' + realBlockers.length + ' blocking overlays when nothing should be open:');
        for (var r = 0; r < realBlockers.length; r++) {
          var info = realBlockers[r];
          console.warn('  → ' + info.tag + '#' + info.id + ' class="' + info.classes + '"',
            'z=' + info.zIndex, info.size, 'pe=' + info.pointerEvents, 'opacity=' + info.opacity);
          // Auto-disable the blocker
          info.el.style.setProperty('pointer-events', 'none', 'important');
          console.warn('  → FORCE DISABLED pointer-events on this element');
        }
      }
    }
  }

  // Run safety net every 500ms
  setInterval(cleanupUI, 500);

  // Monitor click events for stopPropagation interference
  document.addEventListener('click', function (e) {
    console.log('[Rainburg Agent] Click at (' + e.clientX + ',' + e.clientY + ')',
      'target=' + e.target.tagName + '#' + (e.target.id || '') +
      '.' + (e.target.className || '').toString().substring(0, 50),
      'defaultPrevented=' + e.defaultPrevented);
  }, true); // capture phase — fires first

  // Hook into page close buttons once DOM is settled
  setTimeout(function () {
    var cartCloseBtns = document.querySelectorAll('.cart-close, #cartClose, [onclick*="closeCart"], .cart-header button, .cart-header .close');
    for (var i = 0; i < cartCloseBtns.length; i++) {
      cartCloseBtns[i].addEventListener('click', function () {
        setTimeout(cleanupUI, 200);
      });
    }

    var pdpBack = document.getElementById('pdpBack');
    if (pdpBack) {
      pdpBack.addEventListener('click', function () {
        setTimeout(cleanupUI, 200);
      });
    }

    // Watch cart overlay for class changes
    var cartOverlayEl = document.getElementById('cartOverlay');
    if (cartOverlayEl) {
      new MutationObserver(function () {
        if (!cartOverlayEl.classList.contains('open')) cleanupUI();
      }).observe(cartOverlayEl, { attributes: true, attributeFilter: ['class'] });
    }

    // Watch PDP overlay for class changes
    var pdpOverlayEl = document.getElementById('pdpOverlay');
    if (pdpOverlayEl) {
      new MutationObserver(function () {
        if (!pdpOverlayEl.classList.contains('open')) cleanupUI();
      }).observe(pdpOverlayEl, { attributes: true, attributeFilter: ['class'] });
    }
  }, 1500);

  // Keyboard shortcuts
  document.addEventListener('keydown', function (e) {
    // Escape: close agent overlay
    if (e.key === 'Escape') {
      closeOverlay();
      setTimeout(cleanupUI, 200);
    }
    // Ctrl+Shift+U: NUCLEAR UNLOCK — emergency page recovery
    if (e.ctrlKey && e.shiftKey && (e.key === 'U' || e.key === 'u')) {
      e.preventDefault();
      nuclearUnlock();
    }
    // Ctrl+Shift+D: DIAGNOSE — log what's blocking without changing anything
    if (e.ctrlKey && e.shiftKey && (e.key === 'D' || e.key === 'd')) {
      e.preventDefault();
      var blockers = diagnoseBlockers();
      console.log('%c[Rainburg Agent] DIAGNOSIS', 'color:blue;font-size:14px;font-weight:bold');
      console.log('Found ' + blockers.length + ' full-screen fixed elements at page center:');
      for (var d = 0; d < blockers.length; d++) {
        console.log('  ' + (d + 1) + '.', blockers[d].tag + '#' + blockers[d].id,
          'class="' + blockers[d].classes + '"', 'z=' + blockers[d].zIndex,
          'pe=' + blockers[d].pointerEvents, 'opacity=' + blockers[d].opacity,
          blockers[d].size);
      }
      // Also check shadow DOM
      if (elevenLabsEl && elevenLabsEl.shadowRoot) {
        console.log('ElevenLabs shadow DOM accessible — checking...');
        var shadowAll = elevenLabsEl.shadowRoot.querySelectorAll('*');
        var shadowCount = 0;
        for (var ss = 0; ss < shadowAll.length; ss++) {
          var sel = shadowAll[ss];
          var scs = window.getComputedStyle(sel);
          if (scs.position === 'fixed' || scs.position === 'absolute') {
            var sr = sel.getBoundingClientRect();
            if (sr.width > 100 && sr.height > 100) {
              shadowCount++;
              console.log('  Shadow: ' + sel.tagName + '.' + (sel.className || ''),
                Math.round(sr.width) + 'x' + Math.round(sr.height),
                'z=' + scs.zIndex, 'pe=' + scs.pointerEvents, 'opacity=' + scs.opacity);
            }
          }
        }
        if (shadowCount === 0) console.log('  No large shadow DOM elements found');
      } else {
        console.log('ElevenLabs shadow DOM not accessible');
      }
    }
  });

  // ─── CART BADGE UPDATE ─────────────────────────────────────────────────────
  function updateCartBadge() {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var badges = document.querySelectorAll('.bag-count, .cart-count, [data-cart-count]');
        badges.forEach(function (b) {
          b.textContent = cart.item_count;
        });
      })
      .catch(function () {});
  }

  // ─── KEYBOARD SHORTCUT ─────────────────────────────────────────────────────
  document.addEventListener('keydown', function (e) {
    if (e.altKey && e.key === 'a') { btn.click(); }
    if (e.key === 'Escape' && lightbox.classList.contains('visible')) {
      lightbox.classList.remove('visible');
    }
  });

  console.log('[Rainburg Agent] Active. Session:', sessionId, '| Alt+A to toggle');

})();
